import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from '../../../theme';
import { Animated, Image, Linking, Modal, Platform, Pressable, ScrollView, Text, TextInput, View } from "react-native";
import { useSelector } from "react-redux";
import { createApiClient } from "../../../api/apiClient";
import { launchImageLibrary } from "react-native-image-picker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import DLOTooltip from "../../../modules/dlo/components/DLOTooltip";
import HyperlinkText, { validateTextUrls } from "../../../components/HyperlinkText";
 import styles from "../TicketDetailScreen.style";
import SelectField from "./SelectField";
import { getRoleScopeConfig, extractRoleOptionsFromResponse, ApiRoleOption } from "./SubmitTicketFormPanel";
import AccessibleModulesCard from "./AccessibleModulesCard";
import useSupportTierRoles from "../../Admin/pages/UserManagement/hooks/useSupportTierRoles";
import AccessRoleLocationSelector, { AccessRoleLocationValue } from "./AccessRoleLocationSelector";
import {
  normalizeTicketStatusKey,
  normalizeUrgencyKey,
  toTitleCaseWord,
} from "../../../utils/ticketNormalization";
import {
  STATUS_TEXT,
  STATUS_BG,
  STATUS_COLOR,
  URGENCY_BG,
  URGENCY_COLOR,
  STATUS_BG_DARK,
  STATUS_COLOR_DARK,
  URGENCY_BG_DARK,
  URGENCY_COLOR_DARK,
} from "../../../constants/ticketStatusConstants";
import {
  ActivityItem,
  CATEGORY_OPTIONS,
  EditableTicketInfo,
  MARKET_OPTIONS,
  NoteItem,
  RESOLUTION_PATH_OPTIONS,
  SUBCATEGORY_OPTIONS,
  SubmitTicketDynamicField,
  TicketDetailRecord,
  TicketStatus,
} from "../../../store/slices/ticketingPortalSlice";
import type { RootState } from "../../../store";
import {
  selectDistinctLocationNames,
  selectLocationsWithSapId,
  selectDistinctRegions,
  selectDistinctMarkets,
  selectAccessibleModuleNames,
  selectLocationSapIdByName,
} from "../../../store/selectors/appDataSelectors";
import { formatDateWithTimezone, formatDateWithTimezoneNoYear } from "../util/dateFormatter";
import { uploadTicketImage } from "../../../store/thunks/ticketingPortalThunk";
import { uploadTicketFile } from "../../../store/thunks/ticketingPortalThunk";
import type { TeamResponse } from "../../../types/ticketingConfig.types";
 
// ?? helpers ?????????????????????????????????????????????????????????????????
import SelectStakeholdersModal from "./SelectStakeholdersModal";
import { TicketingLocationUser } from "../../../api/ticketApi";

// ── helpers ─────────────────────────────────────────────────────────────────

function formatAttachmentSize(size?: string | number): string {
  const bytes = typeof size === "number" ? size : Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper function to get dynamic label for market/level reference based on ticketLevel
function getLevelReferenceLabel(ticketLevel?: string): string {
  switch (ticketLevel?.toLowerCase()) {
    case "location":
      return "location";
    case "region":
      return "region";
    case "market":
    default:
      return "market";
  }
}

// Use shared constants with local aliases and convert to proper style objects
const URGENCY_BADGE_STYLE = Object.entries(URGENCY_BG).reduce(
  (acc, [key, color]) => ({ ...acc, [key]: { backgroundColor: color } }),
  {} as Record<string, object>
);
const URGENCY_TEXT_COLOR = URGENCY_COLOR;
const STATUS_BADGE_STYLE = Object.entries(STATUS_BG).reduce(
  (acc, [key, color]) => ({ ...acc, [key]: { backgroundColor: color } }),
  {} as Record<string, object>
);
const STATUS_TEXT_COLOR = STATUS_COLOR;

// Dark-mode badge colours. The global Dark stylesheet forces all text white and the
// light pastel badge backgrounds are inline (not swept), so the badges need explicit
// dark backgrounds and must opt out of the white-text rule via data-lh-keep-color.
const KEEP_COLOR_PROPS = { dataSet: { lhKeepColor: "1" } } as any;
const getStatusBadgeColors = (key: string, isDark: boolean) => ({
  bg: (isDark ? STATUS_BG_DARK : STATUS_BG)[key],
  color: (isDark ? STATUS_COLOR_DARK : STATUS_TEXT_COLOR)[key] || "#6b7280",
});
const getUrgencyBadgeColors = (key: string, isDark: boolean) => ({
  bg: (isDark ? URGENCY_BG_DARK : URGENCY_BG)[key],
  color: (isDark ? URGENCY_COLOR_DARK : URGENCY_TEXT_COLOR)[key] || "#374151",
});
// Disabled submit buttons: the light style dims a blue button with opacity, which
// leaves unreadable grey-on-blue in Dark mode.
const DARK_DISABLED_SUBMIT_STYLE = { backgroundColor: "#3A3A3A", opacity: 1 };
// ServiceNow incident pill (header): pale-blue light style -> dark teal surface.
const DARK_SERVICENOW_BADGE_STYLE = { backgroundColor: "#12303D" };
const DARK_SERVICENOW_TEXT_STYLE = { color: "#5CC8F0" };
// Access Request pills: dark surface + light text for each light pastel pill.
const AR_PILL_DARK = {
  primary: { bg: "#0F2A44", text: "#6AB7FF" },
  temporary: { bg: "#2A2347", text: "#B9A9FF" },
  modify: { bg: "#3A2E12", text: "#FFD966" },
  new: { bg: "#132B4A", text: "#7FB8FF" },
  denied: { bg: "#4A1A14", text: "#FF8A78" },
  modification: { bg: "#3D2608", text: "#FFAD5C" },
  approved: { bg: "#17321F", text: "#6EE7A0" },
  grant: { bg: "#103A2A", text: "#6EE7B7" },
  included: { bg: "#132B4A", text: "#7FB8FF" },
  partial: { bg: "#3A2E12", text: "#FFD966" },
} as const;
// Triage card: the light style paints a pale lavender CSS gradient (not swept by the
// dark engine), which leaves white text on a pale surface.
const DARK_TRIAGE_CARD_STYLE = {
  backgroundColor: "#1E1E1E",
  borderColor: "#3A3770",
  ...(Platform.OS === "web"
    ? { backgroundImage: "linear-gradient(180deg, #2A2850 0%, #1E1E1E 60%)" }
    : {}),
} as any;
const EDIT_URGENCY_DOT_COLOR: Record<string, string> = {
  critical: "#c12c01",
  high: "#cc6e16",
  medium: "#fcc42b",
  low: "#a8a8a8",
};
const STAKEHOLDER_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Parses the comma-separated `ticket.stakeholders` string into a clean email list. */
function parseStakeholderList(raw?: string): string[] {
  return (raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// ?? Module-level constants for TicketInfoSection ??????????????????????????????
type DynamicValueItem = { fieldConfigId: number; historicalLabel: string; value: string };

/** Dynamic fields rendered separately in the edit form (not via the generic trigger field loop) */
const EXCLUDED_EDIT_FIELD_NAMES = new Set([
  "number_of_people_impacted", "percent_of_loads_impacted", "secondary_stakeholders",
  "submitter", "category", "sub_category", "title", "issue_description",
  "urgency", "ticket_level", "level_reference", "attachments",
]);

/** Dynamic value field names whose data is already shown via savedInfo top-level props */
const SKIP_VIEW_FIELD_NAMES = new Set([
  "number_of_people_impacted", "percent_of_loads_impacted",
  "secondary_stakeholders", "submitter",
]);

// ?? Ticket Info Section (view + inline edit) ?????????????????????????????????

type TicketInfoSectionProps = {
  ticket: TicketDetailRecord;
  savedInfo: EditableTicketInfo;
  isEditing: boolean;
  draft: EditableTicketInfo;
  editErrors?: Partial<Record<keyof EditableTicketInfo, string>>;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (dynamicValues?: DynamicValueItem[]) => void;
  onPatchDraft: (field: keyof EditableTicketInfo, value: string) => void;
  /** When true, hides the Edit button (ticket is read-only, e.g. closed-merged) */
  isReadOnly?: boolean;
  onAddStakeholder?: (email: string) => void;
  /** Gates the "+ Secondary Stakeholders" control — mirrors Affected Locations' canEditTriage (no assignee requirement). Falls back to isReadOnly when omitted. */
  stakeholdersReadOnly?: boolean;
};

export const TicketInfoSection: React.FC<TicketInfoSectionProps> = ({
  ticket,
  savedInfo,
  isEditing,
  draft,
  editErrors,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onPatchDraft,
  isReadOnly,
  onAddStakeholder,
  stakeholdersReadOnly,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const urgencyValue = savedInfo.urgency || ticket.urgency;
  const urgencyLabel = urgencyValue ? normalizeUrgencyKey(urgencyValue) : "";
  const urgencyBadge = getUrgencyBadgeColors(urgencyLabel, isDark);

  // ?? Redux selectors (same data sources as Submit Ticket Form) ?????????????
  const allDynamicFields = useSelector((state: RootState) => state.ticketingPortal.submitTicketDynamicFields);
  const categoryConfig   = useSelector((state: RootState) => state.ticketingPortal.submitTicketCategoryConfig);
  const apiCategories    = useSelector((state: RootState) => state.ticketingPortal.submitTicketCategories);
  const apiSubCategoryMap = useSelector((state: RootState) => state.ticketingPortal.submitTicketSubCategoryMap);
  const locationNames    = useSelector(selectDistinctLocationNames);
  const locationsWithSapId = useSelector(selectLocationsWithSapId);
  const regionOptions    = useSelector(selectDistinctRegions);
  const marketOptions    = useSelector(selectDistinctMarkets);
  const moduleOptions    = useSelector(selectAccessibleModuleNames);

  const categoryOptions  = apiCategories.length ? apiCategories : (CATEGORY_OPTIONS as unknown as string[]);
  const subCategoryMap   = Object.keys(apiSubCategoryMap).length ? apiSubCategoryMap : (SUBCATEGORY_OPTIONS as Record<string, string[]>);
  const subCategoryOptions = draft.category ? subCategoryMap[draft.category] ?? [] : [];
  const tertiaryCategoryOptions = useMemo(() => {
    const selCat = categoryConfig.find((c) => c.name === draft.category);
    const selSub = selCat?.subCategories?.find((s) => s.name === draft.subcategory);
    return (selSub?.tertiaryCategories ?? [])
      .filter((t) => t.isActive !== false)
      .map((t) => t.name);
  }, [categoryConfig, draft.category, draft.subcategory]);

  const impactedPeopleField = allDynamicFields.find((f) => f.fieldName === "number_of_people_impacted");
  const loadImpactField     = allDynamicFields.find((f) => f.fieldName === "percent_of_loads_impacted");
  const impactedPeopleOptions = [...(impactedPeopleField?.staticOptions ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((o) => o.value || o.label);
  const loadImpactOptions = [...(loadImpactField?.staticOptions ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((o) => o.value || o.label);
  const fallbackLoadImpactOptions     = ["0 - 25%", "26 - 50%", "51 - 75%", "76 - 100%"];
  const fallbackPeopleImpactedOptions = ["1 - 10", "11 - 50", "51 - 100", "100+"];

  // ?? Local state for dynamic field values in edit mode ?????????????????????
  const [editTriggerValues, setEditTriggerValues] = useState<Record<string, string>>({});
  const [textMultiInputs, setTextMultiInputs]     = useState<Record<string, string>>({});
  const [dynFieldErrors, setDynFieldErrors]       = useState<Record<string, string>>({});
  // Tracks whether trigger values have been seeded for the current edit session
  const editSessionSeedRef = useRef(false);

  // ── Secondary Stakeholders manage mode (mirrors Affected Locations "Manage" UI) ──
  const [stakeholderManageMode, setStakeholderManageMode] = useState(false);
  const [stakeholderInput, setStakeholderInput] = useState("");
  const [stakeholderInputError, setStakeholderInputError] = useState<string | null>(null);
  const [showStakeholderPicker, setShowStakeholderPicker] = useState(false);

  const currentStakeholders = useMemo(() => parseStakeholderList(savedInfo.stakeholders), [savedInfo.stakeholders]);
  const isStakeholdersReadOnly = stakeholdersReadOnly ?? isReadOnly;

  // Ticket Level's selected location(s) — scopes the "Select Users" picker the same way
  // as Submit Ticket's Ticket Level does. For Location-level tickets, use the live Affected
  // Locations list (includes any sites added later via Manage Locations), not just the
  // single/multi value captured at submission time in ticket.market.
  const stakeholderLevelValues = useMemo(() => {
    if (ticket.ticketLevel === "Location") {
      const names = ticket.affectedLocations.map((loc) => loc.name).filter(Boolean);
      return Array.from(new Set(names));
    }
    const raw = ticket.market || "";
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [String(parsed)].filter(Boolean);
    } catch {
      return raw.split(",").map((v) => v.trim()).filter(Boolean);
    }
  }, [ticket.ticketLevel, ticket.affectedLocations, ticket.market]);
  const stakeholderScope = {
    regions: ticket.ticketLevel === "Region" ? stakeholderLevelValues : [],
    markets: ticket.ticketLevel === "Market" ? stakeholderLevelValues : [],
    locations: ticket.ticketLevel === "Location" ? stakeholderLevelValues : [],
  };
  const hasStakeholderScope =
    stakeholderScope.regions.length > 0 || stakeholderScope.markets.length > 0 || stakeholderScope.locations.length > 0;

  const handleAttachStakeholders = (selectedUsers: TicketingLocationUser[]) => {
    const existingLower = new Set(currentStakeholders.map((e) => e.toLowerCase()));
    selectedUsers.forEach((user) => {
      const email = user.email.trim();
      if (email && !existingLower.has(email.toLowerCase())) {
        existingLower.add(email.toLowerCase());
        onAddStakeholder?.(email);
      }
    });
  };

  const handleAddStakeholderPress = () => {
    const value = stakeholderInput.trim();
    if (!value) return;
    if (!STAKEHOLDER_EMAIL_REGEX.test(value)) {
      setStakeholderInputError("Please enter a valid email address.");
      return;
    }
    if (currentStakeholders.some((e) => e.toLowerCase() === value.toLowerCase())) {
      setStakeholderInputError("This stakeholder has already been added.");
      return;
    }
    if (currentStakeholders.length >= 10) {
      setStakeholderInputError("You can add up to 10 stakeholder emails.");
      return;
    }
    onAddStakeholder?.(value);
    setStakeholderInput("");
    setStakeholderInputError(null);
  };

  // Seed trigger values from ticket.dynamicValues.
  // Runs when edit mode opens AND re-runs (once) when allDynamicFields loads after edit mode is already open.
  useEffect(() => {
    if (!isEditing) {
      editSessionSeedRef.current = false; // reset for next edit session
      return;
    }
    if (!allDynamicFields.length) return; // wait for fields to load
    if (editSessionSeedRef.current) return; // already seeded � don't overwrite user changes
    editSessionSeedRef.current = true;
    const initial: Record<string, string> = {};
    for (const dv of ticket.dynamicValues ?? []) {
      const field = allDynamicFields.find((f) => f.id === dv.fieldConfigId);
      if (field && dv.value) initial[field.fieldName] = dv.value;
    }
    setEditTriggerValues(initial);
    setTextMultiInputs({});
    setDynFieldErrors({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, allDynamicFields.length]);

  // ?? Active trigger fields (mirrors useSubmitTicketData activeTriggerFields) ?
  const activeTriggerFields = useMemo(() => {
    const selCat    = categoryConfig.find((c) => c.name === draft.category);
    const selCatId  = selCat?.id ?? null;
    const selSubId  = selCat?.subCategories?.find((s) => s.name === draft.subcategory)?.id ?? null;
    return allDynamicFields.filter((field) => {
      if (!field.triggers || field.triggers.length === 0) return false;
      return field.triggers.some((trigger) => {
        const catMatch = trigger.categoryId === null || trigger.categoryId === selCatId;
        const subMatch = trigger.subCategoryId === null || trigger.subCategoryId === selSubId;
        let depMatch = true;
        if (trigger.dependsOnFieldName) {
          const actual = editTriggerValues[trigger.dependsOnFieldName];
          const expected = trigger.dependsOnFieldValues?.length
            ? trigger.dependsOnFieldValues
            : trigger.dependsOnFieldValue ? [trigger.dependsOnFieldValue] : [];
          depMatch = expected.length ? expected.includes(actual) : Boolean(actual);
        }
        if (field.fieldName === "workflow") depMatch = depMatch && editTriggerValues.hud_gui === "HUD";
        if (field.fieldName === "screen")   depMatch = depMatch && editTriggerValues.hud_gui === "GUI";
        return catMatch && subMatch && depMatch;
      });
    });
  }, [allDynamicFields, categoryConfig, draft.category, draft.subcategory, editTriggerValues]);

  /** Dynamic trigger fields to render in the edit form (excludes fields shown elsewhere) */
  const sortedDynamicEditFields = useMemo(
    () => [...activeTriggerFields]
      .filter((f) => !EXCLUDED_EDIT_FIELD_NAMES.has(f.fieldName))
      .sort((a, b) => a.displayOrder - b.displayOrder),
    [activeTriggerFields]
  );

  /** Dynamic values to show in view mode (non-empty, non-excluded) */
  const viewDynamicValues = useMemo(
    () => (ticket.dynamicValues ?? []).filter((dv) => {
      if (!dv.value || dv.fieldNoLongerActive) return false;
      const field = allDynamicFields.find((f) => f.id === dv.fieldConfigId);
      return !(field && SKIP_VIEW_FIELD_NAMES.has(field.fieldName));
    }),
    [ticket.dynamicValues, allDynamicFields]
  );

  // ?? text_multi helpers ????????????????????????????????????????????????????
  const getTextMultiTags = (fieldName: string): string[] => {
    const raw = editTriggerValues[fieldName] ?? "";
    if (!raw) return [];
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
  };
  const addTextMultiTag = (fieldName: string) => {
    const val = (textMultiInputs[fieldName] ?? "").trim();
    if (!val) return;
    const existing = getTextMultiTags(fieldName);
    if (existing.includes(val)) return;
    setEditTriggerValues((prev) => ({ ...prev, [fieldName]: JSON.stringify([...existing, val]) }));
    setTextMultiInputs((prev) => ({ ...prev, [fieldName]: "" }));
  };
  const removeTextMultiTag = (fieldName: string, tag: string) => {
    const updated = getTextMultiTags(fieldName).filter((t) => t !== tag);
    setEditTriggerValues((prev) => ({ ...prev, [fieldName]: updated.length ? JSON.stringify(updated) : "" }));
  };

  /** Resolves the value for a dynamic field � mirrors getRequiredFieldValue in SubmitTicketScreen */
  const getEditFieldValue = (fieldName: string): string => {
    if (fieldName === "number_of_people_impacted") return draft.peopleImpacted || "";
    if (fieldName === "percent_of_loads_impacted") return draft.loadsImpacted || "";
    if (fieldName === "secondary_stakeholders")    return draft.stakeholders || "";
    return editTriggerValues[fieldName] ?? "";
  };

  /** Build dynamicValues array (all active trigger fields, including People/Loads Impacted) and save */
  const handleSaveEditWithDynamic = () => {
    const builtDynamicValues: DynamicValueItem[] = activeTriggerFields
      .map((f) => ({ fieldConfigId: f.id, historicalLabel: f.fieldLabel, value: getEditFieldValue(f.fieldName) }))
      .filter((item) => Boolean(item.value));
    onSaveEdit(builtDynamicValues);
  };

  const levelReferenceLabel =
    draft.ticketLevel === "Location" ? "Location"
    : draft.ticketLevel === "Region" ? "Region"
    : "Market";

  const levelReferenceOptions = useMemo(() => {
    const currentValue = draft.market?.trim();
    if (draft.ticketLevel === "Location") {
      const base = locationsWithSapId.length ? locationsWithSapId : ticket.affectedLocations.map((l) => l.name);
      const opts = [...base, ticket.location, currentValue].filter(
        (v): v is string => Boolean(v && v.trim().length > 0)
      );
      return Array.from(new Set(opts));
    }
    if (draft.ticketLevel === "Region") {
      const base = regionOptions.length ? regionOptions : [];
      return Array.from(new Set([...base, currentValue].filter((v): v is string => Boolean(v?.trim()))));
    }
    const base = marketOptions.length ? marketOptions : [...MARKET_OPTIONS];
    return Array.from(new Set([...base, currentValue].filter((v): v is string => Boolean(v?.trim()))));
  }, [draft.market, draft.ticketLevel, ticket.affectedLocations, ticket.location, locationsWithSapId, regionOptions, marketOptions]);

  return (
    <View style={styles.detailSection}>
      {/* Section header */}
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <MaterialIcons name="description" size={16} color="#374151" />
          <Text style={styles.sectionTitle}>Ticket Information</Text>
        </View>
        {!isEditing ? (
          !isReadOnly ? (
          <Pressable
            style={({ pressed }: { pressed: boolean }) => [styles.btnGhostSm, pressed && styles.btnPressed]}
            onPress={onStartEdit}
          >
            <MaterialIcons name="edit" size={13} color="#374151" />
            <Text style={styles.btnGhostSmText}>Edit</Text>
          </Pressable>
          ) : null
        ) : (
          <View style={styles.editActions}>
            <Pressable
              style={({ pressed }: { pressed: boolean }) => [styles.btnGhostSm, pressed && styles.btnPressed]}
              onPress={onCancelEdit}
            >
              <Text style={styles.btnGhostSmText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={({ pressed }: { pressed: boolean }) => [styles.btnPrimary, pressed && styles.btnPressed]}
              onPress={handleSaveEditWithDynamic}
            >
              <Text style={styles.btnPrimaryText}>Save Changes</Text>
            </Pressable>
          </View>
        )}
      </View>

      <View style={styles.sectionBody}>
        {!isEditing ? (
          /* VIEW MODE */
          <View style={styles.kvGrid}>
            <KVPair label="Category" value={savedInfo.category} />
            <KVPair label="Sub-Category" value={savedInfo.subcategory} />
            {savedInfo.tertiaryCategory ? (
              <KVPair label="Tertiary Category" value={savedInfo.tertiaryCategory} />
            ) : null}
            <KVPair label="Ticket Level" value={savedInfo.ticketLevel} />
            <KVPair label={toTitleCaseWord(getLevelReferenceLabel(savedInfo.ticketLevel))} value={savedInfo.market} />
            <View style={styles.kvItem}>
              <Text style={styles.kvLabel}>Urgency</Text>
              <View style={[styles.badgeSm, urgencyBadge.bg ? { backgroundColor: urgencyBadge.bg } : {}]}>
                <View style={[styles.badgeDotSm, { backgroundColor: urgencyBadge.color }]} />
                <Text style={[styles.badgeSmText, { color: urgencyBadge.color }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>
                  {urgencyLabel ? toTitleCaseWord(urgencyLabel) : "-"}
                </Text>
              </View>
            </View>
            <KVPair label="People Impacted" value={savedInfo.peopleImpacted} />
            <KVPair label="Loads Impacted" value={savedInfo.loadsImpacted} />
            {ticket.mmsToggle !== undefined && ticket.mmsToggle !== null ? (
              <KVPair label="Stopping my ability to Make, Move, Sell" value={ticket.mmsToggle ? "Yes" : "No"} />
            ) : null}
            {/* Dynamic field values stored with this ticket */}
            {viewDynamicValues.map((dv) => (
              <KVPair key={`dv-${dv.fieldConfigId}`} label={dv.historicalLabel} value={dv.value} />
            ))}
            {ticket.mergeImpactNote ? (
              <View style={styles.mergeImpactNote}>
                <MaterialIcons name="info-outline" size={13} color="#8B6508" />
                <Text style={styles.mergeImpactNoteText}>{ticket.mergeImpactNote}</Text>
              </View>
            ) : null}
            <View style={[styles.kvItem, styles.kvItemFull]}>
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <Text style={styles.kvLabel}>Secondary Stakeholders</Text>
                {!isStakeholdersReadOnly && (
                  !stakeholderManageMode ? (
                    <Pressable
                      style={({ pressed }: { pressed: boolean }) => [styles.manageLocationsBtn, pressed && styles.btnPressed]}
                      onPress={() => setStakeholderManageMode(true)}
                    >
                      <MaterialIcons name="add" size={14} color="#2D2D2D" />
                      <Text style={styles.manageLocationsBtnText}>Secondary Stakeholders</Text>
                    </Pressable>
                  ) : (
                    <Pressable
                      style={({ pressed }: { pressed: boolean }) => [styles.doneBtn, pressed && styles.btnPressed]}
                      onPress={() => {
                        setStakeholderManageMode(false);
                        setStakeholderInput("");
                        setStakeholderInputError(null);
                      }}
                    >
                      <Text style={styles.doneBtnText}>Done</Text>
                    </Pressable>
                  )
                )}
              </View>

              {!stakeholderManageMode ? (
                <Text style={styles.kvValue}>{savedInfo.stakeholders || "None"}</Text>
              ) : (
                <View style={{ marginTop: 8 }}>
                  <View style={[styles.locationAddRow, { alignItems: "center" }]}>
                    <TextInput
                      style={[
                        styles.editInput,
                        { flex: 1, backgroundColor: "#f8fafc", color: "#9ca3af" },
                        isDark && { backgroundColor: "#1A1A1A", borderColor: "#3A3A3A" },
                      ]}
                      value=""
                      editable={false}
                      placeholder="Use Select Users to add stakeholders"
                      placeholderTextColor={isDark ? "#8C8C8C" : "#9aa4b2"}
                    />
                    <Pressable
                      onPress={() => hasStakeholderScope && setShowStakeholderPicker(true)}
                      disabled={!hasStakeholderScope}
                      style={({ pressed }: { pressed: boolean }) => [
                        {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                          borderWidth: 1,
                          borderColor: "#006fce",
                          borderRadius: 8,
                          paddingHorizontal: 12,
                          paddingVertical: 9,
                          opacity: hasStakeholderScope ? 1 : 0.5,
                        },
                        pressed && hasStakeholderScope ? { opacity: 0.8 } : null,
                      ]}
                    >
                      <MaterialIcons name="people-outline" size={16} color="#006fce" />
                      <Text style={{ fontSize: 13, fontWeight: "700", color: "#006fce" }}>Select Users</Text>
                    </Pressable>
                  </View>
                  <SelectStakeholdersModal
                    visible={showStakeholderPicker}
                    onClose={() => setShowStakeholderPicker(false)}
                    regions={stakeholderScope.regions}
                    markets={stakeholderScope.markets}
                    locations={stakeholderScope.locations}
                    existingEmails={currentStakeholders}
                    onAttach={handleAttachStakeholders}
                  />
                  {!hasStakeholderScope ? (
                    <Text style={[styles.kvLabel, { textTransform: "none", marginTop: 6, color: "#818181" }]}>
                      Select a Ticket Level location to enable Select Users.
                    </Text>
                  ) : null}
                  {stakeholderInputError ? (
                    <Text style={[styles.kvLabel, { color: "#c12c01", textTransform: "none", marginTop: 6 }]}>{stakeholderInputError}</Text>
                  ) : null}

                  <Text style={[styles.locationFieldLabel, { marginTop: 12 }]}>Current stakeholders</Text>
                  <View style={styles.locationGrid}>
                    {currentStakeholders.length === 0 ? (
                      <Text style={styles.kvValue}>No secondary stakeholders added yet.</Text>
                    ) : (
                      currentStakeholders.map((email) => (
                        <View key={email} style={styles.locationCard}>
                          <Text style={styles.locationCardName} numberOfLines={1}>{email}</Text>
                        </View>
                      ))
                    )}
                  </View>
                </View>
              )}
            </View>
            <KVPair label="Issue Description" value={savedInfo.description} fullWidth isDescription />
            {(ticket.attachments ?? []).length > 0 ? (
              <View style={[styles.kvItem, styles.kvItemFull]}>
                <Text style={styles.kvLabel}>Attachments</Text>
                <View style={styles.kvAttachmentList}>
                  {(ticket.attachments ?? []).map((attachment) => {
                    const fileSizeLabel = formatAttachmentSize(attachment.fileSize);
                    const hasUrl = Boolean(attachment.storageUrl);
                    return (
                      <View key={`att-${attachment.id}-${attachment.fileName}`} style={styles.kvAttachmentRow}>
                        {hasUrl ? (
                          <Pressable onPress={() => Linking.openURL(String(attachment.storageUrl))}>
                            <Text style={styles.kvAttachmentLink}>{attachment.fileName}</Text>
                          </Pressable>
                        ) : (
                          <Text style={styles.kvValue}>{attachment.fileName}</Text>
                        )}
                        {fileSizeLabel ? <Text style={styles.kvAttachmentSize}>({fileSizeLabel})</Text> : null}
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : null}
          </View>
        ) : (
          /* EDIT MODE � mirrors SubmitTicketFormPanel field rendering with live API config */
          <View style={styles.editForm}>
            {/* Category / Sub-Category */}
            <View style={styles.formRow}>
              <View style={styles.editFieldWrap}>
                <EditSelectField
                  label="Category"
                  value={draft.category}
                  options={categoryOptions}
                  required
                  placeholder="Select category..."
                  onChange={(value) => {
                    const validSubs = subCategoryMap[value] ?? [];
                    onPatchDraft("category", value);
                    if (!validSubs.includes(draft.subcategory)) onPatchDraft("subcategory", "");
                    onPatchDraft("tertiaryCategory", "");
                    setEditTriggerValues({});
                  }}
                />
                {editErrors?.category ? <Text style={styles.editErrorText}>{editErrors.category}</Text> : null}
              </View>
              <View style={styles.editFieldWrap}>
                <EditSelectField
                  label="Sub-Category"
                  value={draft.subcategory}
                  options={subCategoryOptions}
                  required
                  disabled={!draft.category || !subCategoryOptions.length}
                  placeholder={draft.category ? "Select sub-category..." : "Select a category first"}
                  onChange={(value) => { onPatchDraft("subcategory", value); onPatchDraft("tertiaryCategory", ""); setEditTriggerValues({}); }}
                />
                {editErrors?.subcategory ? <Text style={styles.editErrorText}>{editErrors.subcategory}</Text> : null}
              </View>
            </View>

            {tertiaryCategoryOptions.length > 0 ? (
              <View style={styles.formRow}>
                <View style={styles.editFieldWrap}>
                  <EditSelectField
                    label="Tertiary Category"
                    value={draft.tertiaryCategory ?? ""}
                    options={tertiaryCategoryOptions}
                    placeholder="Select a tertiary category..."
                    onChange={(value) => onPatchDraft("tertiaryCategory", value)}
                  />
                </View>
              </View>
            ) : null}

            {/* System Issue: People & Loads Impacted */}
            {draft.category === "System Issue" ? (
              <View style={styles.formRow}>
                <View style={styles.editFieldWrap}>
                  <EditSelectField
                    label={impactedPeopleField?.fieldLabel || "Number of People Impacted"}
                    value={draft.peopleImpacted}
                    options={impactedPeopleOptions.length ? impactedPeopleOptions : fallbackPeopleImpactedOptions}
                    required
                    placeholder="Select impact..."
                    onChange={(value) => onPatchDraft("peopleImpacted", value)}
                  />
                  {editErrors?.peopleImpacted ? <Text style={styles.editErrorText}>{editErrors.peopleImpacted}</Text> : null}
                </View>
                <View style={styles.editFieldWrap}>
                  <EditSelectField
                    label={loadImpactField?.fieldLabel || "Percent of Loads Impacted"}
                    value={draft.loadsImpacted}
                    options={loadImpactOptions.length ? loadImpactOptions : fallbackLoadImpactOptions}
                    required
                    placeholder="Select load impact..."
                    onChange={(value) => onPatchDraft("loadsImpacted", value)}
                  />
                  {editErrors?.loadsImpacted ? <Text style={styles.editErrorText}>{editErrors.loadsImpacted}</Text> : null}
                </View>
              </View>
            ) : null}

            {/* Dynamic trigger fields � same rendering logic as SubmitTicketFormPanel */}
            {sortedDynamicEditFields.map((field) => {
              const fieldValue  = editTriggerValues[field.fieldName] ?? "";
              const staticOpts  = [...(field.staticOptions ?? [])]
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((o) => o.value || o.label);
              const isLocField  = field.dropdownSource === "navigator_location_api";
              const isModField  = field.fieldName === "navigator_module";
              // Option-source priority: location API ? appData module list (with staticOpts fallback) ? static options from config
              const selectOptions = isLocField
                ? locationNames
                : isModField
                ? (moduleOptions.length ? moduleOptions : staticOpts)
                : staticOpts;

              if (field.fieldType === "single_select" || field.fieldType === "single_select_searchable") {
                return (
                  <View key={field.fieldName} style={styles.formRow}>
                    <View style={[styles.editFieldWrap, styles.editFieldFull]}>
                      <EditSelectField
                        label={field.fieldLabel}
                        value={fieldValue}
                        options={selectOptions}
                        required={field.required}
                        disabled={!selectOptions.length}
                        placeholder={field.placeholder || "Select..."}
                        onChange={(v) => setEditTriggerValues((prev) => ({ ...prev, [field.fieldName]: v }))}
                      />
                      {field.tooltip ? <Text style={styles.helpText}>{field.tooltip}</Text> : null}
                      {dynFieldErrors[field.fieldName] ? <Text style={styles.editErrorText}>{dynFieldErrors[field.fieldName]}</Text> : null}
                    </View>
                  </View>
                );
              }

              if (field.fieldType === "text_multi") {
                const tags = getTextMultiTags(field.fieldName);
                const currentInput = textMultiInputs[field.fieldName] ?? "";
                return (
                  <View key={field.fieldName} style={styles.formRow}>
                    <View style={[styles.editFieldWrap, styles.editFieldFull]}>
                      <Text style={styles.editFieldLabel}>
                        {field.fieldLabel}{field.required ? <Text style={styles.editRequiredMark}> *</Text> : null}
                      </Text>
                      <View style={[styles.editInput, { flexDirection: "row", alignItems: "center", paddingVertical: 0, paddingHorizontal: 0 }]}>
                        <TextInput
                          style={[styles.editInput, { flex: 1, borderWidth: 0, marginBottom: 0, paddingRight: 36 }]}
                          value={currentInput}
                          onChangeText={(v) => setTextMultiInputs((prev) => ({ ...prev, [field.fieldName]: v }))}
                          onSubmitEditing={() => addTextMultiTag(field.fieldName)}
                          blurOnSubmit={false}
                          placeholder={field.placeholder || "Enter value and press Enter"}
                          placeholderTextColor="#9aa4b2"
                          returnKeyType="done"
                        />
                        <Pressable
                          onPress={() => addTextMultiTag(field.fieldName)}
                          style={{ position: "absolute" as const, right: 8, top: 0, bottom: 0, justifyContent: "center" as const, alignItems: "center" as const, paddingHorizontal: 6 }}
                        >
                          <Text style={{ fontSize: 18, color: currentInput.trim() ? "#006fce" : "#9aa4b2", fontWeight: "600" }}>?</Text>
                        </Pressable>
                      </View>
                      {tags.length > 0 && (
                        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                          {tags.map((tag) => (
                            <View key={tag} style={{ flexDirection: "row", alignItems: "center", backgroundColor: "#e8f0fe", borderRadius: 16, paddingVertical: 4, paddingLeft: 10, paddingRight: 6, borderWidth: 1, borderColor: "#006fce" }}>
                              <Text style={{ fontSize: 13, color: "#006fce", marginRight: 4 }}>{tag}</Text>
                              <Pressable onPress={() => removeTextMultiTag(field.fieldName, tag)} style={{ width: 18, height: 18, borderRadius: 9, backgroundColor: "#c5d9f5", justifyContent: "center" as const, alignItems: "center" as const }}>
                                <Text style={{ fontSize: 12, color: "#006fce", lineHeight: 18, textAlign: "center" }}>-</Text>
                              </Pressable>
                            </View>
                          ))}
                        </View>
                      )}
                      {field.tooltip ? <Text style={styles.helpText}>{field.tooltip}</Text> : null}
                    </View>
                  </View>
                );
              }

              if (field.fieldType === "text_url") {
                return (
                  <View key={field.fieldName} style={styles.formRow}>
                    <View style={[styles.editFieldWrap, styles.editFieldFull]}>
                      <Text style={styles.editFieldLabel}>
                        {field.fieldLabel}{field.required ? <Text style={styles.editRequiredMark}> *</Text> : null}
                      </Text>
                      <TextInput
                        style={[styles.editInput, dynFieldErrors[field.fieldName] ? { borderColor: "#c12c01" } : null]}
                        value={fieldValue}
                        onChangeText={(v) => setEditTriggerValues((prev) => ({ ...prev, [field.fieldName]: v }))}
                        maxLength={field.charLimit ?? 500}
                        placeholder={field.placeholder || "https://"}
                        placeholderTextColor="#9aa4b2"
                        keyboardType="url"
                        autoCapitalize="none"
                      />
                      {dynFieldErrors[field.fieldName] ? <Text style={styles.editErrorText}>{dynFieldErrors[field.fieldName]}</Text> : null}
                      {field.tooltip ? <Text style={styles.helpText}>{field.tooltip}</Text> : null}
                    </View>
                  </View>
                );
              }

              // Default: plain text input
              return (
                <View key={field.fieldName} style={styles.formRow}>
                  <View style={[styles.editFieldWrap, styles.editFieldFull]}>
                    <Text style={styles.editFieldLabel}>
                      {field.fieldLabel}{field.required ? <Text style={styles.editRequiredMark}> *</Text> : null}
                    </Text>
                    <TextInput
                      style={styles.editInput}
                      value={fieldValue}
                      onChangeText={(v) => setEditTriggerValues((prev) => ({ ...prev, [field.fieldName]: v }))}
                      maxLength={field.charLimit ?? undefined}
                      placeholder={field.placeholder || "Enter value"}
                      placeholderTextColor="#9aa4b2"
                    />
                    {field.tooltip ? <Text style={styles.helpText}>{field.tooltip}</Text> : null}
                    {dynFieldErrors[field.fieldName] ? <Text style={styles.editErrorText}>{dynFieldErrors[field.fieldName]}</Text> : null}
                  </View>
                </View>
              );
            })}

            {/* Ticket Level / Level Reference */}
            <View style={styles.formRow}>
              <View style={styles.editFieldWrap}>
                <EditSelectField
                  label="Ticket Level"
                  value={draft.ticketLevel}
                  options={["Location", "Market", "Region"]}
                  required
                  placeholder="Select ticket level..."
                  onChange={(value) => { onPatchDraft("ticketLevel", value); onPatchDraft("market", ""); }}
                />
                {editErrors?.ticketLevel ? <Text style={styles.editErrorText}>{editErrors.ticketLevel}</Text> : null}
              </View>
              <View style={styles.editFieldWrap}>
                <EditSelectField
                  label={levelReferenceLabel}
                  value={draft.market}
                  options={levelReferenceOptions}
                  required
                  placeholder={`Select ${levelReferenceLabel.toLowerCase()}...`}
                  onChange={(value) => onPatchDraft("market", value)}
                />
                {editErrors?.market ? <Text style={styles.editErrorText}>{editErrors.market}</Text> : null}
              </View>
            </View>

            {/* Urgency */}
            <View style={styles.formRow}>
              <View style={[styles.editFieldWrap, styles.editFieldFull]}>
                <Text style={styles.editFieldLabel}>
                  Urgency <Text style={styles.editRequiredMark}>*</Text>
                </Text>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  {(["Critical", "High", "Medium", "Low"] as const).map((item) => {
                    const urgencyKey = item.toLowerCase();
                    const active = normalizeUrgencyKey(draft.urgency) === urgencyKey;
                    return (
                      <Pressable
                        key={item}
                        onPress={() => onPatchDraft("urgency", item)}
                        style={({ pressed }) => [{
                          borderWidth: 1,
                          borderColor: active ? "#005cbc" : "#d7dfec",
                          backgroundColor: active ? "#e8f0fb" : "#ffffff",
                          borderRadius: 999,
                          paddingHorizontal: 16,
                          paddingVertical: 8,
                          flexDirection: "row" as const,
                          alignItems: "center" as const,
                          gap: 6,
                          opacity: pressed ? 0.88 : 1,
                        }]}
                      >
                        <View
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: EDIT_URGENCY_DOT_COLOR[urgencyKey] ?? "#a8a8a8",
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 12,
                            color: active ? "#034895" : "#4b4b4b",
                            fontWeight: active ? "700" : "600",
                          }}
                        >
                          {item}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            </View>

            {/* Secondary Stakeholders */}
            <EditField
              label="Secondary Stakeholders"
              value={draft.stakeholders}
              onChangeText={(v) => onPatchDraft("stakeholders", v)}
              fullWidth
              helpText="Comma-separated email addresses (max 10)"
            />
            {editErrors?.stakeholders ? <Text style={styles.editErrorText}>{editErrors.stakeholders}</Text> : null}
          </View>
        )}
      </View>
    </View>
  );
};

// ?? KVPair ????????????????????????????????????????????????????????????????????

type KVPairProps = { label: string; value: string; fullWidth?: boolean; isDescription?: boolean };
const KVPair: React.FC<KVPairProps> = ({ label, value, fullWidth, isDescription }) => (
  <View style={[styles.kvItem, fullWidth && styles.kvItemFull]}>
    <Text style={styles.kvLabel}>{label}</Text>
    <Text style={isDescription ? styles.kvValueDescription : styles.kvValue}>{value}</Text>
  </View>
);

// ?? EditField ?????????????????????????????????????????????????????????????????

type EditFieldProps = {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  fullWidth?: boolean;
  multiline?: boolean;
  helpText?: string;
  maxLength?: number;
};

const EditField: React.FC<EditFieldProps> = ({
  label,
  value,
  onChangeText,
  fullWidth,
  multiline,
  helpText,
  maxLength,
}) => (
  <View style={[styles.editFieldWrap, fullWidth && styles.editFieldFull]}>
    <Text style={styles.editFieldLabel}>{label}</Text>
    <TextInput
      style={[styles.editInput, multiline && styles.editTextarea]}
      value={value}
      onChangeText={onChangeText}
      maxLength={maxLength}
      multiline={multiline}
      numberOfLines={multiline ? 5 : 1}
      textAlignVertical={multiline ? "top" : "center"}
    />
    {helpText ? <Text style={styles.helpText}>{helpText}</Text> : null}
  </View>
);

type EditSelectFieldProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

const EditSelectField: React.FC<EditSelectFieldProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = "Select...",
  required,
  disabled,
}) => {
  const [open, setOpen] = useState(false);

  if (Platform.OS === "web") {
    const hasSelectedValue = Boolean(value && options.includes(value));

    return (
      <View style={styles.editFieldWrap}>
        <Text style={styles.editFieldLabel}>
          {label} {required ? <Text style={styles.editRequiredMark}>*</Text> : null}
        </Text>
        {/* Native select on web keeps dropdown clipping/alignment stable inside Ticket Detail layout. */}
        {/* @ts-ignore */}
        <select
          value={hasSelectedValue ? value : ""}
          disabled={disabled}
          onChange={(e: any) => onChange(e.target.value)}
          style={{
            height: 38,
            width: "100%",
            borderRadius: 7,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "#d1d5db",
            paddingLeft: 10,
            paddingRight: 28,
            fontSize: 13,
            backgroundColor: disabled ? "#f8fafc" : "#ffffff",
            color: hasSelectedValue ? "#111827" : "#94a3b8",
            outline: "none",
          } as any}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </View>
    );
  }

  return (
    <View style={styles.editFieldWrap}>
      <Text style={styles.editFieldLabel}>
        {label} {required ? <Text style={styles.editRequiredMark}>*</Text> : null}
      </Text>
      <View style={styles.editSelectWrap}>
        <Pressable
          disabled={disabled}
          onPress={() => setOpen((prev) => !prev)}
          style={({ pressed }) => [
            styles.editSelectTrigger,
            disabled ? styles.editSelectTriggerDisabled : null,
            pressed && !disabled ? styles.btnPressed : null,
          ]}
        >
          <Text style={[styles.editSelectText, !value ? styles.editSelectPlaceholder : null]}>
            {value || placeholder}
          </Text>
          <MaterialIcons
            name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={18}
            color={disabled ? "#9ca3af" : "#6b7280"}
          />
        </Pressable>

        {open && !disabled ? (
          <View style={styles.editSelectMenu}>
            <ScrollView nestedScrollEnabled>
              {options.map((option) => {
                const selected = option === value;
                return (
                  <Pressable
                    key={option}
                    onPress={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.editSelectMenuItem,
                      selected ? styles.editSelectMenuItemActive : null,
                      pressed ? styles.btnPressed : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.editSelectMenuItemText,
                        selected ? styles.editSelectMenuItemTextActive : null,
                      ]}
                    >
                      {option}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ?? Ticket Hero Banner ????????????????????????????????????????????????????????

type TicketHeroProps = {
  ticket: TicketDetailRecord;
  savedInfo: EditableTicketInfo;
  isWidespread?: boolean;
  isReadOnly?: boolean;
  canAccessServiceNow?: boolean;
  onStatusChange?: (status: TicketStatus) => void | Promise<void>;
  onToggleWidespread?: () => void;
  onOpenReopenedTicket?: (ticketId: string) => void;
  /** Active teams (Team Assignment Configuration) available for manual reassignment. */
  assignedTeams?: TeamResponse[];
  /** Resolved display name of the team currently assigned to this ticket, if any. */
  assignedTeamName?: string | null;
  /** True when the currently-assigned team has since been deactivated. */
  isAssignedTeamInactive?: boolean;
  onTeamChange?: (teamId: number) => void | Promise<void>;
};

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "progress", label: "In Progress" },
  { value: "waiting-it", label: "Escalated to IT" },
  { value: "waiting-tmv", label: "Escalated to TMV" },
  { value: "submitted-ado", label: "Submitted to ADO" },
  { value: "waiting-user", label: "Waiting on User" },
  { value: "resolved", label: "Closed" },
];

export const TicketHero: React.FC<TicketHeroProps> = ({
  ticket,
  savedInfo,
  isWidespread,
  isReadOnly,
  canAccessServiceNow,
  onStatusChange,
  onToggleWidespread,
  onOpenReopenedTicket,
  assignedTeams,
  assignedTeamName,
  isAssignedTeamInactive,
  onTeamChange,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const urgencyValue = savedInfo.urgency || ticket.urgency;
  const urgency = urgencyValue ? normalizeUrgencyKey(urgencyValue) : "";
  const status = normalizeTicketStatusKey(ticket.status);
  const statusBadge = getStatusBadgeColors(status, isDark);
  const urgencyHeroBadge = getUrgencyBadgeColors(urgency, isDark);
  const [statusOpen, setStatusOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [pendingTeam, setPendingTeam] = useState<TeamResponse | null>(null);
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const serviceNowUrl = ticket.incidentId
    ? `${process.env.SERVICE_NOW_BASE_URL}/nav_to.do?uri=incident_list.do?sysparm_query=number%3D${encodeURIComponent(ticket.incidentId)}`
    : null;

  const isAccessRequest = ticket.category === "Access Request" && Boolean(ticket.accessRequest);

  return (
    <View style={styles.detailHero}>
      <View style={styles.dhTop}>
        <View style={styles.dhIdBlock}>
          <Text style={styles.dhId}>{ticket.id}</Text>
          {isAccessRequest ? (
            (() => {
              const ar = ticket.accessRequest!;
              const primary = isDark ? AR_PILL_DARK.primary : { bg: "#E6F3FF", text: "#005CBC" };
              const primaryBg = primary.bg; const primaryText = primary.text;
              const sub = (ticket.subcategory || "").toLowerCase();
              let subPill = isDark ? AR_PILL_DARK.new : { bg: "#DDE8FB", text: "#005CBC" };
              if (sub.includes("temporary")) { subPill = isDark ? AR_PILL_DARK.temporary : { bg: "#EDEAFB", text: "#6E56D9" }; }
              else if (sub.includes("modify")) { subPill = isDark ? AR_PILL_DARK.modify : { bg: "#FFF4D9", text: "#8B6A12" }; }
              else if (sub.includes("new")) { subPill = isDark ? AR_PILL_DARK.new : { bg: "#DDE8FB", text: "#005CBC" }; }
              const subBg = subPill.bg; const subText = subPill.text;

              return (
                <>
                  <View style={[styles.badgeSm, { backgroundColor: primaryBg }]}> 
                    <Text style={[styles.badgeSmText, { color: primaryText }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>{ar.accessType || ""}</Text>
                  </View>
                  {ticket.subcategory ? (
                    <View style={[styles.badgeSm, { backgroundColor: subBg, marginLeft: 8 }]}> 
                      <Text style={[styles.badgeSmText, { color: subText }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>{ticket.subcategory}</Text>
                    </View>
                  ) : null}
                  {ticket.accessRequestStatus ? (() => {
                    const arStatusLower = ticket.accessRequestStatus.toLowerCase();
                    const arStatus = arStatusLower.includes("denied")
                      ? (isDark ? AR_PILL_DARK.denied : { bg: "#FEF2F2", text: "#DC2626" })
                      : arStatusLower.includes("modification")
                        ? (isDark ? AR_PILL_DARK.modification : { bg: "#FFF7ED", text: "#C2410C" })
                        : (isDark ? AR_PILL_DARK.approved : { bg: "#F0FDF4", text: "#16A34A" });
                    return (
                      <View style={[styles.badgeSm, { backgroundColor: arStatus.bg, marginLeft: 8 }]}>
                        <Text style={[styles.badgeSmText, { color: arStatus.text }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>{ticket.accessRequestStatus}</Text>
                      </View>
                    );
                  })() : null}
                </>
              );
            })()
          ) : (
            <>
              <View style={[styles.badgeSm, statusBadge.bg ? { backgroundColor: statusBadge.bg } : {}]}>
                <View style={[styles.badgeDotSm, { backgroundColor: statusBadge.color }]} />
                <Text style={[styles.badgeSmText, { color: statusBadge.color }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>
                  {STATUS_TEXT[status] || status}
                </Text>
              </View>
              <View style={[styles.badgeSm, urgencyHeroBadge.bg ? { backgroundColor: urgencyHeroBadge.bg } : {}]}>
                <View style={[styles.badgeDotSm, { backgroundColor: urgencyHeroBadge.color }]} />
                <Text style={[styles.badgeSmText, { color: urgencyHeroBadge.color }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>
                  {toTitleCaseWord(urgency)}
                </Text>
              </View>
              {isReadOnly && assignedTeamName ? (
                <View style={[styles.badgeSm, { backgroundColor: "#EEF2FF" }]}>
                  <Text style={[styles.badgeSmText, { color: "#374151" }]}>{assignedTeamName}</Text>
                </View>
              ) : null}
            </>
          )}
          {ticket.incidentId ? (
            canAccessServiceNow && serviceNowUrl ? (
              <Pressable onPress={() => Linking.openURL(serviceNowUrl)}>
                <View style={[styles.badgeServiceNow, isDark && DARK_SERVICENOW_BADGE_STYLE]}>
                  <Text
                    style={[styles.badgeServiceNowText, isDark && DARK_SERVICENOW_TEXT_STYLE]}
                    {...(isDark ? KEEP_COLOR_PROPS : {})}
                  >
                    {ticket.incidentId}
                  </Text>
                </View>
              </Pressable>
            ) : (
              <View style={[styles.badgeServiceNow, isDark && DARK_SERVICENOW_BADGE_STYLE]}>
                <Text
                  style={[styles.badgeServiceNowText, isDark && DARK_SERVICENOW_TEXT_STYLE]}
                  {...(isDark ? KEEP_COLOR_PROPS : {})}
                >
                  {ticket.incidentId}
                </Text>
                <MaterialIcons name="info-outline" size={13} color={isDark ? "#5CC8F0" : "#0078A7"} style={{ marginLeft: 4 }} />
                {Platform.OS === "web" && (
                  // @ts-ignore web-only title tooltip helper
                  <span title="Contact your IT administrator if you need to view this incident in ServiceNow."></span>
                )}
              </View>
            )
          ) : null}
          {isWidespread ? (
            <View style={[styles.widespreadBadge, isDark && { backgroundColor: URGENCY_BG_DARK.high }]}>
              <MaterialIcons name="warning" size={12} color={isDark ? URGENCY_COLOR_DARK.high : "#CC6E16"} />
              <Text
                style={[styles.widespreadBadgeText, isDark && { color: URGENCY_COLOR_DARK.high }]}
                {...(isDark ? KEEP_COLOR_PROPS : {})}
              >
                WIDESPREAD
              </Text>
            </View>
          ) : null}
        </View>

        {!isReadOnly && (
          <View style={styles.heroTopActions}>
            {isAccessRequest ? (
              // For Access Request tickets show only the status badge on the right
              <View style={[styles.badgeSm, statusBadge.bg ? { backgroundColor: statusBadge.bg } : {}]}>
                <View style={[styles.badgeDotSm, { backgroundColor: statusBadge.color }]} />
                <Text style={[styles.badgeSmText, { color: statusBadge.color }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>
                  {STATUS_TEXT[status] || status}
                </Text>
              </View>
            ) : (
              <>
                {/* Status dropdown */}
                <View style={styles.statusFieldWrap}>
                  <Text style={styles.statusFieldLabel}>STATUS</Text>
                  {Platform.OS === "web" ? (
                    // Native <select> on web � matches HTML reference exactly
                    // @ts-ignore
                    <select
                      value={status}
                      onChange={(e: any) => onStatusChange?.(e.target.value as TicketStatus)}
                      {...(isDark ? { "data-lh-keep-color": "1" } : {})}
                      style={{
                        height: 32,
                        fontSize: 12,
                        fontWeight: "600",
                        paddingTop: 4,
                        paddingBottom: 4,
                        paddingLeft: 10,
                        paddingRight: 28,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: isDark ? "#5A5A5A" : "#D0D0D0",
                        backgroundColor: isDark ? "#2B2B2B" : "#ffffff",
                        color: isDark ? "#FFFFFF" : "#2D2D2D",
                        colorScheme: isDark ? "dark" : "light",
                        minWidth: 140,
                        cursor: "pointer",
                        appearance: "auto",
                      } as any}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <View style={styles.statusSelectWrap}>
                      <Pressable
                        onPress={() => setStatusOpen((p) => !p)}
                        style={({ pressed }: { pressed: boolean }) => [
                          styles.statusSelectTrigger,
                          pressed && styles.btnPressed,
                        ]}
                      >
                        <Text style={styles.statusSelectText}>{STATUS_TEXT[status] || status}</Text>
                        <MaterialIcons
                          name={statusOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                          size={16}
                          color="#585858"
                        />
                      </Pressable>
                      {statusOpen ? (
                        <View style={styles.statusSelectMenu}>
                          {STATUS_OPTIONS.map((opt) => (
                            <Pressable
                              key={opt.value}
                              onPress={() => {
                                onStatusChange?.(opt.value);
                                setStatusOpen(false);
                              }}
                              style={({ pressed }: { pressed: boolean }) => [
                                styles.statusSelectMenuItem,
                                opt.value === status && styles.statusSelectMenuItemActive,
                                pressed && styles.btnPressed,
                              ]}
                            >
                              <Text
                                style={[
                                  styles.statusSelectMenuItemText,
                                  opt.value === status && styles.statusSelectMenuItemTextActive,
                                ]}
                              >
                                {opt.label}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      ) : null}
                    </View>
                  )}
                </View>

                {/* Assigned Team dropdown */}
                {onTeamChange ? (
                <View style={styles.statusFieldWrap}>
                  <Text style={styles.statusFieldLabel}>ASSIGNED TEAM</Text>
                  {Platform.OS === "web" ? (
                    // @ts-ignore
                    <select
                      value={ticket.assignedTeamId ?? ""}
                      onChange={(e: any) => {
                        const id = Number(e.target.value);
                        const team = (assignedTeams ?? []).find((t) => t.id === id);
                        if (team) setPendingTeam(team);
                      }}
                      {...(isDark ? { "data-lh-keep-color": "1" } : {})}
                      style={{
                        height: 32,
                        fontSize: 12,
                        fontWeight: "600",
                        paddingTop: 4,
                        paddingBottom: 4,
                        paddingLeft: 10,
                        paddingRight: 28,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: isDark ? "#5A5A5A" : "#D0D0D0",
                        backgroundColor: isDark ? "#2B2B2B" : "#ffffff",
                        color: isDark ? "#FFFFFF" : "#2D2D2D",
                        colorScheme: isDark ? "dark" : "light",
                        minWidth: 170,
                        cursor: "pointer",
                        appearance: "auto",
                      } as any}
                    >
                      {ticket.assignedTeamId == null ? (
                        <option value="" disabled>Unassigned</option>
                      ) : null}
                      {(assignedTeams ?? []).map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <View style={styles.statusSelectWrap}>
                      <Pressable
                        onPress={() => setTeamOpen((p) => !p)}
                        style={({ pressed }: { pressed: boolean }) => [
                          styles.statusSelectTrigger,
                          pressed && styles.btnPressed,
                        ]}
                      >
                        <Text style={styles.statusSelectText}>{assignedTeamName || "Unassigned"}</Text>
                        <MaterialIcons
                          name={teamOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                          size={16}
                          color="#585858"
                        />
                      </Pressable>
                      {teamOpen ? (
                        <View style={styles.statusSelectMenu}>
                          {(assignedTeams ?? []).map((t) => (
                            <Pressable
                              key={t.id}
                              onPress={() => {
                                setPendingTeam(t);
                                setTeamOpen(false);
                              }}
                              style={({ pressed }: { pressed: boolean }) => [
                                styles.statusSelectMenuItem,
                                t.id === ticket.assignedTeamId && styles.statusSelectMenuItemActive,
                                pressed && styles.btnPressed,
                              ]}
                            >
                              <Text
                                style={[
                                  styles.statusSelectMenuItemText,
                                  t.id === ticket.assignedTeamId && styles.statusSelectMenuItemTextActive,
                                ]}
                              >
                                {t.name}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      ) : null}
                    </View>
                  )}
                </View>
                ) : null}

                {/* Flag Widespread button */}
                <Pressable
                  // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
                  onMouseEnter={() => setHoveredAction("widespread")}
                  onMouseLeave={() => setHoveredAction(null)}
                  onPress={onToggleWidespread}
                  style={({ pressed }: { pressed: boolean }) => [
                    styles.widespreadBtn,
                    isWidespread && styles.widespreadBtnActive,
                    hoveredAction === "widespread" && !isWidespread ? styles.widespreadBtnHover : null,
                    hoveredAction === "widespread" && isWidespread ? styles.widespreadBtnActiveHover : null,
                    pressed && styles.btnPressed,
                  ]}
                >
                  <MaterialIcons
                    name={isWidespread ? "check" : "warning"}
                    size={14}
                    color={isWidespread ? "#ffffff" : "#034895"}
                  />
                  <Text style={[styles.widespreadBtnText, isWidespread && styles.widespreadBtnTextActive]}>
                    {isWidespread ? "Widespread" : "Flag Widespread"}
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        )}
      </View>

      <Text style={styles.dhTitle}>{ticket.title}</Text>

      {isAssignedTeamInactive ? (
        <Text style={styles.inactiveTeamText}>Inactive Team Assignment</Text>
      ) : null}

      {ticket.reopenedFromId ? (
        <View style={styles.reopenedTagWrapWeb}>
          <View style={styles.reopenedTagPillWeb}>
            <MaterialIcons name="refresh" size={12} color="#166534" />
            <Text style={styles.reopenedTagPillTextWeb}>Re-Opened Ticket</Text>
          </View>
          {onOpenReopenedTicket ? (
            <Pressable onPress={() => onOpenReopenedTicket(ticket.reopenedFromId!)}>
              <Text style={styles.reopenedTagLinkWeb}>{ticket.reopenedFromId}</Text>
            </Pressable>
          ) : (
            <Text style={styles.reopenedTagLinkWeb}>{ticket.reopenedFromId}</Text>
          )}
        </View>
      ) : null}

      <View style={styles.dhMeta}>
        {isAccessRequest && ticket.accessRequest ? (
          <Text style={[styles.dhMetaItem, isDark && { color: colors.text.secondary }]}>
            {"For "}
            <Text style={styles.dhMetaLabel}>{ticket.accessRequest.requestedForName || "-"}</Text>
            <Text style={[styles.dhMetaLabel, isDark && { color: colors.text.primary }]}>
              {/* Remove email/delimiters from requestedForName if present (keep only name part) */}
              {(ticket.accessRequest.requestedForName || "-").split(/\s*[◆•]\s*/)[0].split('@')[0].trim() || "-"}
            </Text>
            {" - GPID "}
            <Text style={styles.dhMetaLabel}>{ticket.accessRequest.requestedForGpid || "-"}</Text>
            {" - "}
            <Text style={styles.dhMetaLabel}>{ticket.accessRequest.requestedForEmail || "-"}</Text>
            {"  Submitted by "}
            <Text style={[styles.dhMetaLabel, isDark && { color: colors.text.primary }]}>{ticket.submittedBy}</Text>
          </Text>
        ) : (
          <>
            <Text style={styles.dhMetaItem}>
              {"Submitted "}
              <Text style={styles.dhMetaLabel}>{formatDateWithTimezone(ticket.submitted)}</Text>
              {" by "}
              <Text style={styles.dhMetaLabel}>{ticket.submittedBy}</Text>
            </Text>
            <Text style={styles.dhMetaItem}>
              {"Last updated "}
              <Text style={styles.dhMetaLabel}>{formatDateWithTimezone(ticket.lastUpdated)}</Text>
            </Text>
          </>
        )}
      </View>

      {/* Team reassignment confirmation modal */}
      <Modal
        transparent
        visible={!!pendingTeam}
        onRequestClose={() => setPendingTeam(null)}
        animationType="fade"
        statusBarTranslucent
      >
        <View style={styles.reassignBackdrop}>
          <View style={styles.reassignDialog}>
            <Text style={styles.reassignDialogTitle}>Change Team Assignment?</Text>
            <Text style={styles.reassignDialogBody}>
              You are changing this team assignment, are you sure you wish to move this to the {pendingTeam?.name} Team?
            </Text>
            <View style={styles.reassignDialogActions}>
              <Pressable style={styles.reassignBtnNo} onPress={() => setPendingTeam(null)}>
                <Text style={styles.reassignBtnNoText}>No</Text>
              </Pressable>
              <Pressable
                style={styles.reassignBtnYes}
                onPress={() => {
                  const team = pendingTeam;
                  setPendingTeam(null);
                  if (team) onTeamChange?.(team.id);
                }}
              >
                <Text style={styles.reassignBtnYesText}>Yes</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ?? Triage Card (sustain-only) ????????????????????????????????????????????????
// RESOLUTION_PATH_OPTIONS is imported from ticketingPortalSlice � single source of truth

const ASSIGNED_TO_OPTIONS = [
  "Unassigned",
  "Maria Chen (me)",
  "Derek Park",
  "Sam Torres",
  "Jordan Lee",
];

// Eligible category/subcategory combinations for ADO integration
const ADO_ELIGIBLE_COMBOS: { category: string; subcategory: string }[] = [
  { category: "Reporting", subcategory: "New" },
  { category: "Reporting", subcategory: "Enhancement" },
  { category: "Reporting", subcategory: "Fix" },
  { category: "Vision Pick (WES)", subcategory: "System Issue" },
  { category: "Navigator", subcategory: "System Issue" },
  { category: "Navigator", subcategory: "System Enhancement" },
];

function isAdoEligibleCombo(category: string, subcategory: string): boolean {
  return ADO_ELIGIBLE_COMBOS.some(
    (c) => c.category === category && c.subcategory === subcategory
  );
}

type TriageCardProps = {
  ticket: TicketDetailRecord;
  activity?: ActivityItem[];
  resolutionPath: string;
  assignedTo: string;
  incidentNumber: string;
  canAccessServiceNow?: boolean;
  assignedToOptions?: string[];
  onResolutionPathChange: (v: string) => void;
  onAssignedToChange: (v: string) => void;
  onIncidentNumberChange: (v: string) => void;
  onMerge: () => void;
  onCreateADO: () => void;
  onManageImpact: () => void;
  onResolve: () => void;
  onSendUpdate: () => Promise<{ msg: string; type: "success" | "info" | "warning" | "error" }>;
  hasChanges: boolean;
  isSending: boolean;
  /** When true, hides all triage action controls */
  isReadOnly?: boolean;
};

export const TriageCard: React.FC<TriageCardProps> = ({
  ticket,
  activity = [],
  resolutionPath,
  assignedTo,
  incidentNumber,
  canAccessServiceNow,
  assignedToOptions,
  onResolutionPathChange,
  onAssignedToChange,
  onIncidentNumberChange,
  onMerge,
  onCreateADO,
  onManageImpact,
  onResolve,
  onSendUpdate,
  hasChanges,
  isSending,
  isReadOnly,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const normalizedStatus = normalizeTicketStatusKey(ticket.status);
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [assignedOpen, setAssignedOpen] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const resolvedAssignedToOptions = assignedToOptions && assignedToOptions.length > 0
    ? assignedToOptions
    : ASSIGNED_TO_OPTIONS;

  const isUnassigned = !assignedTo || assignedTo === "Unassigned";
  const adoComboEligible = isAdoEligibleCombo(ticket.category, ticket.subcategory);
  const adoButtonEnabled = adoComboEligible && !isUnassigned;
  
  // Construct tooltip message based on why button is disabled
  let adoDisabledTooltip = "";
  if (isUnassigned) {
    adoDisabledTooltip = "Ticket must be assigned to a Sustain Team member";
    if (!adoComboEligible) {
      adoDisabledTooltip += ". ADO integration not available for this ticket type.";
    }
  } else if (!adoComboEligible) {
    adoDisabledTooltip = "ADO integration not available for this ticket type.";
  }
  const serviceNowUrl = incidentNumber
    ? `${process.env.SERVICE_NOW_BASE_URL}/nav_to.do?uri=incident_list.do?sysparm_query=number%3D${encodeURIComponent(incidentNumber)}`
    : null;

  const adoWorkItemId = ticket.adoId;

  // Determine resolution path display
  const isResolutionPathUncategorized = !resolutionPath || resolutionPath.trim() === "";
  const getResolutionPathDisplay = (): string => {
    if (isResolutionPathUncategorized) return "Uncategorized";
    if (resolutionPath === "Requires IT" && incidentNumber) {
      return `Routed to IT - ServiceNow Incident: ${incidentNumber}`;
    }
    return resolutionPath;
  };

  const resolutionPathDisplay = getResolutionPathDisplay();

  return (
    <View style={[styles.triageCard, isDark && DARK_TRIAGE_CARD_STYLE]}>
      {/* Card label */}
      <View style={styles.triageCardHeader}>
        <MaterialIcons name="star-outline" size={14} color="#7871E2" />
        <Text style={styles.triageCardLabel}>SUSTAIN TRIAGE</Text>
      </View>

      <View style={styles.triageBody}>
        {/* Triage fields row */}
        <View style={styles.triageRow}>
          {/* Resolution Path */}
          <View style={styles.triageFieldWrap}>
            <View style={styles.triageLabelRow}>
              <Text style={styles.triageLabel}>Resolution Path</Text>
              {isResolutionPathUncategorized && !isReadOnly && (
                <MaterialIcons name="warning" size={14} color="#f59e0b" />
              )}
            </View>
            {isReadOnly ? (
              <View style={styles.triageReadOnlyWrap}>
                <Text style={[
                  styles.triageReadOnlyText,
                  isResolutionPathUncategorized && styles.uncategorizedText,
                ]}>
                  {resolutionPathDisplay}
                </Text>
              </View>
            ) : (
            <View style={styles.triageSelectWrap}>
              <Pressable
                onPress={() => { setResolutionOpen((p) => !p); setAssignedOpen(false); }}
                style={({ pressed }: { pressed: boolean }) => [
                  styles.triageSelectTrigger,
                  pressed && styles.btnPressed,
                ]}
              >
                <Text style={[
                  styles.triageSelectText,
                  isResolutionPathUncategorized && styles.uncategorizedText,
                ]}>
                  {resolutionPathDisplay}
                </Text>
                <MaterialIcons
                  name={resolutionOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                  size={16}
                  color="#374151"
                />
              </Pressable>
              {resolutionOpen ? (
                <View style={styles.triageSelectMenu}>
                  {!resolutionPath && (
                    <Pressable
                      key="uncategorized"
                      onPress={() => { onResolutionPathChange(""); setResolutionOpen(false); }}
                      style={({ pressed }: { pressed: boolean }) => [
                        styles.triageSelectMenuItem,
                        !resolutionPath && styles.triageSelectMenuItemActive,
                        pressed && styles.btnPressed,
                      ]}
                    >
                      <Text style={[
                        styles.triageSelectMenuItemText,
                        !resolutionPath && styles.triageSelectMenuItemTextActive,
                      ]}>
                        Uncategorized
                      </Text>
                    </Pressable>
                  )}
                  {RESOLUTION_PATH_OPTIONS.map((opt) => (
                    <Pressable
                      key={opt}
                      onPress={() => { onResolutionPathChange(opt); setResolutionOpen(false); }}
                      style={({ pressed }: { pressed: boolean }) => [
                        styles.triageSelectMenuItem,
                        opt === resolutionPath && styles.triageSelectMenuItemActive,
                        pressed && styles.btnPressed,
                      ]}
                    >
                      <Text
                        style={[
                          styles.triageSelectMenuItemText,
                          opt === resolutionPath && styles.triageSelectMenuItemTextActive,
                        ]}
                      >
                        {opt}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View>
            )}
          </View>

          {/* Assigned To */}
          <View style={styles.triageFieldWrap}>
            <Text style={styles.triageLabel}>Assigned To</Text>
            {isReadOnly ? (
              <View style={styles.triageReadOnlyWrap}>
                <Text style={styles.triageReadOnlyText}>{assignedTo || "-"}</Text>
              </View>
            ) : (
            <View style={styles.triageSelectWrap}>
              <Pressable
                onPress={() => { setAssignedOpen((p) => !p); setResolutionOpen(false); }}
                style={({ pressed }: { pressed: boolean }) => [
                  styles.triageSelectTrigger,
                  pressed && styles.btnPressed,
                ]}
              >
                <Text style={styles.triageSelectText}>{assignedTo}</Text>
                <MaterialIcons
                  name={assignedOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                  size={16}
                  color="#374151"
                />
              </Pressable>
              {assignedOpen ? (
                <View style={styles.triageSelectMenu}>
                  {resolvedAssignedToOptions
                    .filter((opt) => {
                      // Show "Unassigned" only when no assignment is made
                      if (opt === "Unassigned") {
                        return !assignedTo || assignedTo === "Unassigned";
                      }
                      return true;
                    })
                    .map((opt) => (
                    <Pressable
                      key={opt}
                      onPress={() => { onAssignedToChange(opt); setAssignedOpen(false); }}
                      style={({ pressed }: { pressed: boolean }) => [
                        styles.triageSelectMenuItem,
                        opt === assignedTo && styles.triageSelectMenuItemActive,
                        pressed && styles.btnPressed,
                      ]}
                    >
                      <Text
                        style={[
                          styles.triageSelectMenuItemText,
                          opt === assignedTo && styles.triageSelectMenuItemTextActive,
                        ]}
                      >
                        {opt}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View>
            )}
          </View>

            {/* ServiceNow Incident # � always shown when resolution path is "Requires IT".
              Editable (max 11 chars) when no incidentId is stored yet; read-only once linked. */}
          {resolutionPath === "Requires IT" && (
            <View style={styles.triageFieldWrap}>
              <Text style={styles.triageLabel}>ServiceNow Incident #</Text>
              {ticket.incidentId ? (
                /* Already linked � read-only display */
                <View style={styles.triageReadOnlyWrap}>
                  {canAccessServiceNow && serviceNowUrl ? (
                    <Pressable onPress={() => Linking.openURL(serviceNowUrl)}>
                      <Text style={styles.adoLinkText}>{incidentNumber}</Text>
                    </Pressable>
                  ) : (
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                      <Text style={styles.triageReadOnlyText}>{incidentNumber}</Text>
                      <MaterialIcons
                        name="info-outline"
                        size={15}
                        color="#818181"
                        style={{ marginLeft: 4 }}
                      />
                      {Platform.OS === "web" && (
                        // @ts-ignore web-only title tooltip helper
                        <span title="Contact your IT administrator if you need to view this incident in ServiceNow."></span>
                      )}
                    </View>
                  )}
                </View>
              ) : isReadOnly ? (
                /* Read-only mode without an incident number */
                <View style={styles.triageReadOnlyWrap}>
                  <Text style={[styles.triageReadOnlyText, { color: "#9ca3af" }]}>-</Text>
                </View>
              ) : (
                /* Editable input � accepts up to 15-character ServiceNow incident number */
                <TextInput
                  style={[
                    styles.triageSelectTrigger,
                    {
                      fontSize: 13,
                      color: incidentNumber ? "#111827" : "#9ca3af",
                      fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
                      letterSpacing: 0.5,
                    },
                  ]}
                  value={incidentNumber}
                  onChangeText={(v) => onIncidentNumberChange(v.replace(/[^a-zA-Z0-9]/g, "").slice(0, 11))}
                  placeholder="e.g. INC0012345"
                  placeholderTextColor="#9ca3af"
                  maxLength={11}
                  autoCapitalize="characters"
                  autoCorrect={false}
                />
              )}
            </View>
          )}
        </View>

        {/* Uncategorized warning message */}
        {isResolutionPathUncategorized && !isReadOnly && (
          <View style={styles.triageWarningBox}>
            <MaterialIcons name="warning" size={14} color="#f59e0b" />
            <Text style={styles.triageWarningText}>
              Ticket requires categorization for proper routing.
            </Text>
          </View>
        )}

        {canAccessServiceNow && ticket.incidentId ? (
          <View style={styles.triageSyncRow}>
            <View style={styles.syncBadge}>
              <Text style={styles.syncDot}>?</Text>
              <Text style={styles.syncBadgeText}>Synced with ServiceNow</Text>
            </View>
            <Text style={styles.syncInfoText}>
              {`Last sync: ${formatDateWithTimezone(activity.find((a) => a.type === "servicenow")?.time ?? ticket.lastUpdated)}`}{"\n"}
              {"ServiceNow status: "}
              <Text style={styles.syncStatusText}>
                {(() => {
                  switch (normalizeTicketStatusKey(ticket.status)) {
                    case "new":           return "New";
                    case "progress":      return "In Progress";
                    case "waiting-it":    return "On Hold";
                    case "waiting-tmv":   return "On Hold";
                    case "waiting-user":  return "On Hold";
                    case "submitted-ado": return "In Progress";
                    case "resolved":      return "Resolved";
                    case "closed-merged": return "Closed";
                    default:              return "In Progress";
                  }
                })()}
              </Text>
            </Text>
          </View>
        ) : null}

        {/* Action buttons row � hidden when ticket is read-only (e.g. closed-merged) */}
        {!isReadOnly && (
        <View style={styles.triageActions}>
          <View style={styles.triageActionsLeft}>
            {normalizedStatus !== "resolved" && normalizedStatus !== "closed-merged" && (
            <Pressable
              // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
              onMouseEnter={() => setHoveredButton("merge")}
              onMouseLeave={() => setHoveredButton(null)}
              onPress={onMerge}
              style={({ pressed }: { pressed: boolean }) => [
                styles.btnSecondary,
                hoveredButton === "merge" && styles.btnSecondaryHover,
                pressed && styles.btnPressed,
              ]}
            >
              <MaterialIcons name="merge-type" size={14} color="#034895" />
              <Text style={styles.btnSecondaryText}>Merge Tickets</Text>
            </Pressable>
            )}
            {adoWorkItemId ? (
              /* ADO already created � show read-only linked field */
              <View style={styles.triageAdoLinkWrap}>
                <Text style={styles.triageLabel}>ADO Work Item</Text>
                {/* ADO access logic: show as link or plain text with tooltip */}
                {ticket.hasAdoAccess !== false ? (
                  <Pressable
                    onPress={() => {
                      const isWESProject =
                        ticket.category === "Vision Pick (WES)" &&
                        ticket.subcategory === "System Issue";
                      const baseLink = isWESProject
                        ? "https://dev.azure.com/PepsiCoIT/WES/_workitems/edit"
                        : "https://dev.azure.com/PepsiCoIT2/PBNA_WSE_LIGHTHOUSE/_workitems/edit";
                      Linking.openURL(`${baseLink}/${adoWorkItemId.replace("ADO-", "")}`);
                    }}
                    // @ts-ignore
                    {...(Platform.OS === "web" ? { title: `Open ${adoWorkItemId} in Azure DevOps` } : {})}
                  >
                    <Text style={styles.adoLinkText}>{adoWorkItemId}</Text>
                  </Pressable>
                ) : (
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Text style={styles.adoLinkText}>{adoWorkItemId}</Text>
                    <MaterialIcons
                      name="info-outline"
                      size={15}
                      color="#818181"
                      style={{ marginLeft: 4 }}
                    />
                    {/* Tooltip for web only */}
                    {Platform.OS === "web" && (
                      <span style={{ marginLeft: 4 }} title="Contact your team lead to access this item in Azure DevOps."></span>
                    )}
                  </View>
                )}
              </View>
            ) : (
              /* ADO not yet created � show button, disabled when not eligible */
              <DLOTooltip
                content={adoDisabledTooltip}
                position="bottom"
                disabled={adoButtonEnabled}
              >
                <Pressable
                  // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
                  onMouseEnter={() => setHoveredButton("ado")}
                  onMouseLeave={() => setHoveredButton(null)}
                  onPress={adoButtonEnabled ? onCreateADO : undefined}
                  style={({ pressed }: { pressed: boolean }) => [
                    styles.btnSecondary,
                    adoButtonEnabled && hoveredButton === "ado" && styles.btnSecondaryHover,
                    adoButtonEnabled && pressed && styles.btnPressed,
                    !adoButtonEnabled && styles.btnDisabled,
                  ]}
                >
                  <MaterialIcons name="bug-report" size={14} color={adoButtonEnabled ? "#034895" : "#9ca3af"} />
                  <Text style={[styles.btnSecondaryText, !adoButtonEnabled && { color: "#9ca3af" }]}>Create ADO Item</Text>
                </Pressable>
              </DLOTooltip>
            )}
            <Pressable
              // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
              onMouseEnter={() => setHoveredButton("impact")}
              onMouseLeave={() => setHoveredButton(null)}
              onPress={onManageImpact}
              style={({ pressed }: { pressed: boolean }) => [
                styles.btnSecondary,
                hoveredButton === "impact" && styles.btnSecondaryHover,
                pressed && styles.btnPressed,
              ]}
            >
              <MaterialIcons name="location-on" size={14} color="#034895" />
              <Text style={styles.btnSecondaryText}>Manage Impact</Text>
            </Pressable>
          </View>
          <View style={styles.triageActionsRight}>
            {/* Send Update button � appears when there are changes to triage fields */}
            {hasChanges && (
              <Pressable
                onPress={onSendUpdate}
                disabled={isSending}
                // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
                onMouseEnter={() => setHoveredButton("sendUpdate")}
                onMouseLeave={() => setHoveredButton(null)}
                style={({ pressed }: { pressed: boolean }) => [
                  styles.btnSecondary,
                  hoveredButton === "sendUpdate" && !isSending && styles.btnSecondaryHover,
                  isSending && styles.btnDisabled,
                  pressed && styles.btnPressed,
                ]}
              >
                <MaterialIcons 
                  name={isSending ? "hourglass-empty" : "send"} 
                  size={14} 
                  color={isSending ? "#9ca3af" : "#034895"} 
                />
                <Text style={[styles.btnSecondaryText, isSending && { color: "#9ca3af" }]}>
                  {isSending ? "Sending..." : "Send Update"}
                </Text>
              </Pressable>
            )}
            <Pressable
              // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
              onMouseEnter={() => setHoveredButton("resolve")}
              onMouseLeave={() => setHoveredButton(null)}
              onPress={onResolve}
              style={({ pressed }: { pressed: boolean }) => [
                styles.btnPrimary,
                hoveredButton === "resolve" && styles.btnPrimaryHover,
                pressed && styles.btnPressed,
              ]}
            >
              <MaterialIcons name="check-circle" size={14} color="#ffffff" />
              <Text style={styles.btnPrimaryText}>Resolve Ticket</Text>
            </Pressable>
          </View>
        </View>
        )}
      </View>
    </View>
  );
};

// ?? Access Request Triage Card ?????????????????????????????????????????????????

type AccessRequestTriageCardProps = {
  ticket: TicketDetailRecord;
  isReadOnly?: boolean;
  /** Controlled � whether the edit/modify panel is open in the detail section */
  isEditingDetails?: boolean;
  /** Called when Modify is toggled so the parent can sync detail section edit state */
  onModifyToggle?: (open: boolean) => void;
  onApprove?: (modifyNotes?: string) => void;
  onModify?: (notes: string) => void;
  onDeny?: (reason: string) => void;
  /** Called whenever the modification notes text changes (lets parent lift the value) */
  onModifyNotesChange?: (notes: string) => void;
  /** Called when "Resolved by IT" is clicked */
  onResolvedByIT?: () => void;
};

export const AccessRequestTriageCard: React.FC<AccessRequestTriageCardProps> = ({
  ticket,
  isReadOnly,
  isEditingDetails,
  onModifyToggle,
  onApprove,
  onModify,
  onDeny,
  onModifyNotesChange,
  onResolvedByIT,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  // modifyOpen is now controlled from parent when onModifyToggle is provided
  const isControlled = typeof onModifyToggle === "function";
  const [modifyOpenLocal, setModifyOpenLocal] = useState(false);
  const modifyOpen = isControlled ? Boolean(isEditingDetails) : modifyOpenLocal;

  const [modifyNotes, setModifyNotes] = useState("");
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [simulateFailure, setSimulateFailure] = useState(false);

  const normalizedStatus = normalizeTicketStatusKey(ticket.status);
  /** True when the ticket has been escalated to IT � triage actions are disabled, show "Resolved by IT" */
  const isEscalatedToIT = normalizedStatus === "waiting-it";
  const isDecisionMade = normalizedStatus === "resolved" || normalizedStatus === "closed-merged" || isReadOnly;
  /** All 3 main action buttons are disabled when escalated or a decision was already made */
  const actionsDisabled = isDecisionMade || isEscalatedToIT;
  const approveEnabled = !actionsDisabled && (!modifyOpen || modifyNotes.trim().length > 0);

  const handleModifyNotesChange = (text: string) => {
    setModifyNotes(text);
    onModifyNotesChange?.(text);
  };

  const handleApprove = () => {
    if (!approveEnabled) return;
    onApprove?.(modifyOpen ? modifyNotes : undefined);
  };

  const handleModifyToggle = () => {
    if (actionsDisabled) return;
    const next = !modifyOpen;
    if (isControlled) {
      onModifyToggle!(next);
    } else {
      setModifyOpenLocal(next);
    }
    if (!next) setModifyNotes("");
  };

  const handleDenyPress = () => {
    if (actionsDisabled) return;
    setDenyModalOpen(true);
  };

  const handleDenyConfirm = () => {
    if (!denyReason.trim()) return;
    onDeny?.(denyReason.trim());
    setDenyModalOpen(false);
    setDenyReason("");
  };

  return (
    <View style={[styles.triageCard, isDark && DARK_TRIAGE_CARD_STYLE]}>
      {/* Header row */}
      <View style={styles.arTriageHeader}>
        <View style={styles.arTriageHeaderLeft}>
          <MaterialIcons name="security" size={14} color="#7871E2" />
          <Text style={styles.triageCardLabel}>SUSTAIN TRIAGE</Text>
        </View>
        {/* Simulate provisioning failure checkbox */}
        {/* <Pressable
          style={styles.arTriageCheckboxRow}
          onPress={() => setSimulateFailure((p) => !p)}
          // @ts-ignore
          {...(Platform.OS === "web" ? { title: "Toggle provisioning failure simulation" } : {})}
        >
          <View style={[styles.arTriageCheckbox, simulateFailure && styles.arTriageCheckboxChecked]}>
            {simulateFailure && <MaterialIcons name="check" size={10} color="#ffffff" />}
          </View>
          <Text style={styles.arTriageCheckboxLabel}>Simulate provisioning failure</Text>
        </Pressable> */}
      </View>

      {/* Divider */}
      <View style={styles.arTriageDivider} />

      {/* Action buttons */}
      <View style={styles.arTriageActionsRow}>
        {/* Approve & Provision */}
        <Pressable
          style={({ pressed }: { pressed: boolean }) => [
            styles.arBtnApprove,
            !approveEnabled && styles.arBtnApproveDisabled,
            pressed && approveEnabled && styles.btnPressed,
          ]}
          onPress={handleApprove}
          disabled={!approveEnabled}
          // @ts-ignore
          {...(Platform.OS === "web" && modifyOpen && !modifyNotes.trim()
            ? { title: "Add modification notes before approving" }
            : {})}
        >
          <MaterialIcons name="check" size={15} color="#ffffff" />
          <Text style={styles.arBtnApproveText}>Approve &amp; Provision</Text>
        </Pressable>

        {/* Modify */}
        <Pressable
          style={({ pressed }: { pressed: boolean }) => [
            styles.arBtnModify,
            actionsDisabled && styles.arBtnModifyDisabled,
            modifyOpen && !actionsDisabled && { backgroundColor: "#EEF2FF" },
            pressed && !actionsDisabled && styles.btnPressed,
          ]}
          onPress={handleModifyToggle}
          disabled={actionsDisabled}
        >
          <MaterialIcons name="edit" size={15} color={actionsDisabled ? "#9ca3af" : "#034895"} />
          <Text style={[styles.arBtnModifyText, actionsDisabled && styles.arBtnDisabledText]}>Modify</Text>
        </Pressable>

        {/* Deny */}
        <Pressable
          style={({ pressed }: { pressed: boolean }) => [
            styles.arBtnDeny,
            actionsDisabled && styles.arBtnModifyDisabled,
            pressed && !actionsDisabled && styles.btnPressed,
          ]}
          onPress={handleDenyPress}
          disabled={actionsDisabled}
        >
          <MaterialIcons name="close" size={15} color={actionsDisabled ? "#9ca3af" : "#374151"} />
          <Text style={[styles.arBtnDenyText, actionsDisabled && styles.arBtnDisabledText]}>Deny</Text>
        </Pressable>
      </View>

      {/* Resolved by IT � shown when ticket is Waiting on IT / Escalated to IT */}
      {isEscalatedToIT && (
        <Pressable
          style={({ pressed }: { pressed: boolean }) => [
            styles.arBtnResolvedByIT,
            pressed && styles.btnPressed,
          ]}
          onPress={onResolvedByIT}
        >
          <MaterialIcons name="check-circle" size={16} color="#ffffff" />
          <Text style={styles.arBtnResolvedByITText}>Resolved by IT</Text>
        </Pressable>
      )}

      {/* Modification notes � shown when Modify is active */}
      {modifyOpen && (
        <View style={styles.arModifyNotesWrap}>
          <View style={styles.arModifyNotesLabelRow}>
            <Text style={styles.arModifyNotesLabel}>MODIFICATION NOTES</Text>
            <Text style={styles.arModifyNotesRequired}> *</Text>
          </View>
          <TextInput
            style={styles.arModifyNotesInput}
            value={modifyNotes}
            onChangeText={handleModifyNotesChange}
            placeholder="Why the request was changed (visible to the requester)"
            placeholderTextColor="#9ca3af"
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
          <Text style={styles.arModifyNotesHint}>Logged to the ticket and shown to the requester.</Text>
        </View>
      )}

      {/* Deny Modal */}
      <Modal
        transparent
        visible={denyModalOpen}
        onRequestClose={() => setDenyModalOpen(false)}
        animationType="fade"
        statusBarTranslucent
      >
        <Pressable
          style={styles.arDenyModalBackdrop}
          onPress={() => setDenyModalOpen(false)}
        >
          <Pressable
            style={styles.arDenyModal}
            // Prevent backdrop close when tapping inside modal
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={styles.arDenyModalTitle}>
              Denial reason (recorded on the ticket and shown to the requester):
            </Text>
            <TextInput
              style={styles.arDenyInput}
              value={denyReason}
              onChangeText={setDenyReason}
              placeholder="Enter denial reason..."
              placeholderTextColor="#9ca3af"
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              autoFocus
            />
            <View style={styles.arDenyActions}>
              <Pressable
                style={({ pressed }: { pressed: boolean }) => [
                  styles.arDenyCancelBtn,
                  pressed && styles.btnPressed,
                ]}
                onPress={() => { setDenyModalOpen(false); setDenyReason(""); }}
              >
                <Text style={styles.arDenyCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={({ pressed }: { pressed: boolean }) => [
                  styles.arDenyOkBtn,
                  !denyReason.trim() && styles.btnDisabled,
                  pressed && denyReason.trim() && styles.btnPressed,
                ]}
                onPress={handleDenyConfirm}
                disabled={!denyReason.trim()}
              >
                <Text style={styles.arDenyOkText}>OK</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

// ?? Access Request Detail Section ??????????????????????????????????????????????

/** Derive a human-readable scope + tier description from access type and location type */
function getAccessScopeTierLabel(locationType?: string, accessType?: string, role?: string, roles: ApiRoleOption[] = []): string {
  // Developer and Super User always have full access regardless of location scope
  if (accessType === "Developer" || accessType === "Super User") {
    return "Full Access";
  }
  // Full/region-scoped roles (e.g. COE, Region Leader/Division) don't carry a location
  // list, so check the role's own scope before falling back to location-type inference.
  const roleScope = role ? getRoleScopeConfig(role, roles) : null;
  if (roleScope?.type === "full") return "Full Access - All locations globally.";
  if (roleScope?.type === "region") return "Full - Region";
  // For Normal User, scope is determined by location type
  switch ((locationType || "").toUpperCase()) {
    case "LOCATION": return "Limited - Location (≤5)";
    case "MARKET":   return "Full - Market";
    case "REGION":   return "Full - Region";
    default:         return role ? `Limited - Location` : "-";
  }
}

function getAccessLocationValue(loc: any): string {
  const type = String(loc?.locationType ?? "").toUpperCase();
  if (type === "MARKET") {
    return String(loc?.market ?? loc?.locationName ?? loc?.locationId ?? "").trim();
  }
  if (type === "REGION") {
    return String(loc?.region ?? loc?.locationName ?? loc?.locationId ?? "").trim();
  }
  return String(loc?.locationName ?? loc?.locationId ?? "").trim();
}

/** Group flat AccessRequestLocationDetail array into a region?market?site hierarchy */
type LocationNode = {
  id: string;
  name: string;
  locationType: "LOCATION" | "MARKET" | "REGION";
  children?: LocationNode[] | undefined;
};

function groupAccessLocations(
  locations: Array<{
    locationType: "LOCATION" | "MARKET" | "REGION";
    locationId: string | null;
    locationName: string | null;
    region?: string | null;
    market?: string | null;
  }>
): LocationNode[] {
  // If all are REGION type � return flat list of regions
  if (locations.every((l) => l.locationType === "REGION")) {
    return locations.map((l, idx) => {
      const value = getAccessLocationValue(l) || `Region ${idx + 1}`;
      return {
      id: value,
      name: value,
      locationType: "REGION",
    }});
  }
  // If all are MARKET type � return flat list of markets
  if (locations.every((l) => l.locationType === "MARKET")) {
    return locations.map((l, idx) => {
      const value = getAccessLocationValue(l) || `Market ${idx + 1}`;
      return {
      id: value,
      name: value,
      locationType: "MARKET",
    }});
  }
  // Mixed mode: group known LOCATION rows by region ? market ? site and keep REGION/MARKET rows visible.
  const regionMap: Record<string, { name: string; markets: Record<string, { name: string; sites: LocationNode[] }> }> = {};
  for (const [index, loc] of locations.entries()) {
    const type = String(loc.locationType || "").toUpperCase();
    const regionKey = String(loc.region ?? "").trim() || "MFG";
    const marketKey = String(loc.market ?? "").trim() || "MFG";
    if (!regionMap[regionKey]) regionMap[regionKey] = { name: regionKey, markets: {} };

    if (!regionMap[regionKey].markets[marketKey]) {
      regionMap[regionKey].markets[marketKey] = { name: marketKey, sites: [] };
    }

    if (type !== "LOCATION") {
      continue;
    }

    const siteName = getAccessLocationValue(loc) || `Location ${index + 1}`;
    regionMap[regionKey].markets[marketKey].sites.push({
      id: String(loc.locationId ?? loc.locationName ?? siteName),
      name: siteName,
      locationType: "LOCATION",
    });
  }
  return Object.entries(regionMap).map(([rKey, rVal]) => ({
    id: rKey,
    name: rVal.name,
    locationType: "REGION" as const,
    children: Object.entries(rVal.markets).map(([mKey, mVal]) => ({
      id: mKey,
      name: mVal.name,
      locationType: "MARKET" as const,
      children: mVal.sites,
    })),
  }));
}

/** A coverage description per row level */
function getCoverage(node: LocationNode): string {
  if (node.locationType === "REGION") {
    const total = (node.children || []).reduce((sum, m) => sum + (m.children?.length || 0), 0);
    return total > 0 ? `${total} sites - expand to view markets` : "Region access";
  }
  if (node.locationType === "MARKET") {
    const total = node.children?.length || 0;
    return total > 0 ? `${total} sites - expand to view sites` : "Market access";
  }
  return "Assigned site - field execution views";
}

/** Recursive tree row renderer */
type SitesTreeRowProps = {
  node: LocationNode;
  depth: number;
  expandedIds: Record<string, boolean>;
  selectedIds: Record<string, boolean>;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string) => void;
  isReadOnly: boolean;
  isLast: boolean;
};

const SitesTreeRow: React.FC<SitesTreeRowProps> = ({
  node, depth, expandedIds, selectedIds, onToggleExpand, onToggleSelect, isReadOnly, isLast,
}) => {
  const isExpanded = Boolean(expandedIds[node.id]);
  const isSelected = Boolean(selectedIds[node.id]);
  const hasChildren = Boolean(node.children?.length);
  const isDark = useTheme().resolvedTheme === "dark";

  const paddingLeft = 14 + depth * 16;
  // Count selected children for partial status badge
  const countSelectedInSubtree = (n: LocationNode): number => {
    if (!n.children) return selectedIds[n.id] ? 1 : 0;
    return n.children.reduce((sum, child) => sum + countSelectedInSubtree(child), 0);
  };
  const totalSitesInSubtree = (n: LocationNode): number => {
    if (!n.children) return 1;
    return n.children.reduce((sum, child) => sum + totalSitesInSubtree(child), 0);
  };

  const selectedInSubtree = countSelectedInSubtree(node);
  const totalInSubtree = totalSitesInSubtree(node);
  const hasPartial = hasChildren && selectedInSubtree > 0 && selectedInSubtree < totalInSubtree;
  const hasAll = hasChildren && selectedInSubtree > 0 && selectedInSubtree === totalInSubtree;

  // Only top-level REGION rows are actual grants (the unit being provisioned).
  // Everything nested underneath (markets, locations) is simply included as
  // part of that region grant, so those rows should read "Included" instead.
  const isTopLevelRegion = depth === 0 && node.locationType === "REGION";

  const statusContent = (() => {
    if (hasAll || isSelected) {
      const grantPill = isDark ? { backgroundColor: AR_PILL_DARK.grant.bg } : undefined;
      const grantText = isDark ? { color: AR_PILL_DARK.grant.text } : undefined;
      const label = isTopLevelRegion ? "Grant" : "Included";
      return (
        <View style={[styles.arSitesStatusGrant, grantPill]}>
          <Text style={[styles.arSitesStatusGrantText, grantText]} {...(isDark ? KEEP_COLOR_PROPS : {})}>{label}</Text>
        </View>
      );
    }
    if (hasPartial) {
      const partialPill = isDark ? { backgroundColor: AR_PILL_DARK.partial.bg } : undefined;
      const partialText = isDark ? { color: AR_PILL_DARK.partial.text } : undefined;
      return (
        <View style={[styles.arSitesPartialStatus, partialPill]}>
          <Text style={[styles.arSitesPartialStatusText, partialText]} {...(isDark ? KEEP_COLOR_PROPS : {})}>{selectedInSubtree}/{totalInSubtree}</Text>
        </View>
      );
    }
    return <Text style={styles.arSitesStatusNone}>-</Text>;
  })();

  const nameStyle = depth === 0
    ? styles.arSitesRowName
    : depth === 1
      ? [styles.arSitesRowName, { fontSize: 13, color: "#374151" }]
      : styles.arSitesRowNameSite;

  return (
    <>
      <View
        style={[
          styles.arSitesRow,
          isLast && !isExpanded && styles.arSitesRowLast,
          { paddingLeft },
        ]}
      >
        {/* Expand/collapse toggle */}
        <Pressable
          style={[styles.arSitesExpandBtn, !hasChildren && { opacity: 0 }]}
          onPress={() => hasChildren && onToggleExpand(node.id)}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
        >
          <MaterialIcons
            name={isExpanded ? "keyboard-arrow-down" : "chevron-right"}
            size={16}
            color="#374151"
          />
        </Pressable>

        {/* Checkbox (sites only for location-scoped roles; also top-level Region/Market rows for
            Region- or Market-scoped access requests, which render as flat depth-0 nodes) */}
        {node.locationType === "LOCATION" || (depth === 0 && (node.locationType === "REGION" || node.locationType === "MARKET")) ? (
          <Pressable
            style={[styles.arSitesCheckbox, (isSelected || hasAll) && styles.arSitesCheckboxChecked]}
            onPress={() => !isReadOnly && onToggleSelect(node.id)}
            disabled={isReadOnly}
          >
            {(isSelected || hasAll) && <MaterialIcons name="check" size={10} color="#ffffff" />}
          </Pressable>
        ) : (
          <View style={{ width: 24 }} />
        )}

        {/* Name */}
        <Text style={nameStyle} numberOfLines={1}>
          {String(node.name || "").trim() || "MFG"}
          {hasChildren && (
            <Text style={styles.arSitesRowCount}>{" "}({totalInSubtree} sites)</Text>
          )}
        </Text>

        {/* Coverage */}
        <Text style={styles.arSitesCoverage} numberOfLines={1}>
          {getCoverage(node)}
        </Text>

        {/* Status */}
        <View style={styles.arSitesStatus}>
          {statusContent}
        </View>
      </View>

      {/* Render children if expanded */}
      {isExpanded && node.children?.map((child, idx) => (
        <SitesTreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          expandedIds={expandedIds}
          selectedIds={selectedIds}
          onToggleExpand={onToggleExpand}
          onToggleSelect={onToggleSelect}
          isReadOnly={isReadOnly}
          isLast={idx === (node.children?.length || 0) - 1}
        />
      ))}
    </>
  );
};

type AccessRequestDetailSectionProps = {
  ticket: TicketDetailRecord;
  isReadOnly?: boolean;
  /** When true, shows editable form fields below the read-only KV grid */
  isEditing?: boolean;
  /** Callback fired with the latest edited values so the caller can collect them */
  onEditChange?: (changes: {
    accessType?: string;
    organization?: string;
    department?: string;
    role?: string;
    roleId?: number;
    /** IDs of currently selected locations in the tree (locationId values) */
    selectedLocationIds?: string[];
    /** Full location objects (with region/market context) for the current selection */
    selectedLocations?: any[];
  }) => void;
  /** Lifted modification notes from the triage card � used to enable/disable Approve button */
  modifyNotes?: string;
  /** Called when Approve button in the details card is clicked */
  onApproveModify?: () => void;
};

export const AccessRequestDetailSection: React.FC<AccessRequestDetailSectionProps> = ({
  ticket,
  isReadOnly,
  isEditing,
  onEditChange,
  modifyNotes,
  onApproveModify,
}) => {
  const ar = ticket.accessRequest;
  if (!ar) return null;

  const subcategoryLower = (ticket.subcategory || "").toLowerCase();
  const isModify = subcategoryLower.includes("modify") || subcategoryLower.includes("change");
  const isTemporary = subcategoryLower.includes("temp");
  const isNew = !isModify && !isTemporary;

  // ── Dynamically fetched roles (replaces hardcoded AR_DEPARTMENT_ROLES / ROLE_SCOPE_MAP) ──
  // Roles for the ticket's own department, used to resolve ar.role / ar.currentRole scope.
  const [arDeptRoles, setArDeptRoles] = useState<ApiRoleOption[]>([]);
  useEffect(() => {
    if (!ar.department) {
      setArDeptRoles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = createApiClient();
        const res = await api.get("/admin/users/roles", { params: { department: ar.department } });
        if (!cancelled) setArDeptRoles(extractRoleOptionsFromResponse(res.data));
      } catch {
        if (!cancelled) setArDeptRoles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ar.department]);

  const combinedRoleOptions = arDeptRoles;

  const locations: Array<{
      locationType: "LOCATION" | "MARKET" | "REGION";
      locationId: string | null;
      locationName: string | null;
      region?: string | null;
      market?: string | null;
    }> = ar.locations || [];
  // Derive scope tier label from access type and first location
  const firstLoc = locations[0];
  const scopeTierLabel = getAccessScopeTierLabel(firstLoc?.locationType, ar.accessType, ar.role, combinedRoleOptions);

  // ?? CURRENT ACCESS scope (independent of the requested role/scope above) ??
  // The current-access side of the comparison must reflect ar.currentRole's own
  // scope, not the requested role's. Super User/Developer roles always have
  // full access (no location list shown); Region-scoped roles show accessible
  // regions only; every other role is location-scoped (shows up to 5 sites).
  const currentRoleScope = ar.currentRole ? getRoleScopeConfig(ar.currentRole, combinedRoleOptions) : null;
  const currentScopeTierLabel = !ar.currentRole
    ? "-"
    : currentRoleScope?.type === "full"
      ? "Full"
      : currentRoleScope?.type === "region"
        ? "Full - Region"
        : "Limited - Location (?5)";
  const currentAccessLocationChips = (() => {
    if (!currentRoleScope || currentRoleScope.type === "full") {
      return [] as string[];
    }
    if (currentRoleScope.type === "region") {
      const regionNames = locations
        .filter((l) => l.locationType === "REGION")
        .map((l) => getAccessLocationValue(l))
        .filter(Boolean);
      return Array.from(new Set(regionNames));
    }
    // market/site-scoped roles � show up to 5 location names
    return (ar.locations || [])
      .map((l: any) => l.locationName)
      .filter(Boolean)
      .slice(0, 5);
  })();

  // Build location tree for sites table
  const locationTree = useMemo(() => groupAccessLocations(locations), [locations]);

  // Expanded state for the tree
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>(() => {
    // Expand top-level nodes by default
    const init: Record<string, boolean> = {};
    locationTree.forEach((n) => { init[n.id] = true; });
    return init;
  });

  // Selected state (pre-populate with all requested locations for review)
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    // Build a set of requested site ids/names from ticket accessRequest to pre-select
    const requested = new Set<string>();
    (ar.locations || []).forEach((rl: any) => {
      if (rl.locationId) requested.add(String(rl.locationId));
      if (rl.locationName) requested.add(String(rl.locationName));
      if (rl.market) requested.add(String(rl.market));
      if (rl.region) requested.add(String(rl.region));
      const resolved = getAccessLocationValue(rl);
      if (resolved) requested.add(resolved);
    });

    // Recursively walk tree and mark nodes selected when they match requested ids/names.
    // Region/Market-scoped access requests produce flat REGION/MARKET nodes (no
    // LOCATION children), so those types must also be matched here � otherwise
    // their checkboxes render as unselected even though they were granted.
    const walk = (nodes: LocationNode[]) => {
      for (const n of nodes) {
        if (n.locationType === "LOCATION" || n.locationType === "REGION" || n.locationType === "MARKET") {
          if (requested.has(n.id) || requested.has(n.name)) init[n.id] = true;
        }
        if (n.children) walk(n.children);
      }
    };

    try {
      walk(locationTree);
    } catch (e) {
      // fallback: no-op
    }

    return init;
  });

  // Control which dropdown is open so we can raise its parent zIndex (matches SubmitTicket behavior)
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const handleDropdownToggle = useCallback((id: string | null) => {
    setActiveDropdownId(id);
  }, []);

  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Count selected sites � count leaf nodes in the tree (not container region/market
  // rows), so selecting a region checkbox doesn't double-count against its children.
  const countLeafNodes = (nodes: LocationNode[]): number =>
    nodes.reduce((sum, n) => sum + (n.children && n.children.length ? countLeafNodes(n.children) : 1), 0);
  const countSelectedLeafNodes = (nodes: LocationNode[]): number =>
    nodes.reduce((sum, n) => {
      if (n.children && n.children.length) return sum + countSelectedLeafNodes(n.children);
      return sum + (selectedIds[n.id] ? 1 : 0);
    }, 0);
  const totalSites = useMemo(() => countLeafNodes(locationTree), [locationTree]);
  const countSelected = useMemo(
    () => countSelectedLeafNodes(locationTree),
    [locationTree, selectedIds]
  );

  const supportTierRoles = useSupportTierRoles("access-request").supportTierRoles;
  const [editedRole, setEditedRole] = useState<{ role: string; roleId: number | null } | null>(null);
  useEffect(() => { if (!isEditing) setEditedRole(null); }, [isEditing]);
  const displayRole = (isEditing && editedRole ? editedRole.role : ar.role) || "";
  const displayRoleScope = displayRole ? getRoleScopeConfig(displayRole, combinedRoleOptions) : null;
  const displayRoleId = useMemo(() => {
    if (isEditing && editedRole) return editedRole.roleId;
    const target = displayRole.trim().toLowerCase();
    const field = combinedRoleOptions.find((r) => r.name.trim().toLowerCase() === target);
    const support = supportTierRoles.find((r) => r.roleName.trim().toLowerCase() === target);
    const id = Number(field?.id ?? support?.roleId);
    return Number.isFinite(id) ? id : null;
  }, [isEditing, editedRole, displayRole, combinedRoleOptions, supportTierRoles]);

  const handleSelectorChange = useCallback((v: AccessRoleLocationValue) => {
    setEditedRole({ role: v.role, roleId: v.roleId });
    onEditChange?.({
      accessType: v.accessType,
      organization: v.organization,
      department: v.department,
      role: v.role,
      roleId: v.roleId ?? undefined,
      selectedLocationIds: v.selectedLocationNames,
      selectedLocations: v.selectedLocations,
    });
  }, [onEditChange]);

  // Title based on subcategory
  const cardTitle = isModify
    ? "Requested change - review & grant"
    : isTemporary
      ? "Temporary access - review & grant"
      : "Requested access - review & grant";

  // Scope limit hint
  const scopeLimit = firstLoc?.locationType === "LOCATION" ? 5 : null;
  const scopeGrantLabel = firstLoc?.locationType === "REGION"
    ? "Grant access - one or more regions"
    : firstLoc?.locationType === "MARKET"
      ? "Grant access - one or more markets"
      : `Grant access - up to ${scopeLimit ?? 5} sites`;
  const requestedRoleScope = ar.role ? getRoleScopeConfig(ar.role, combinedRoleOptions) : null;

  // Chips for current / requested locations � resolve a display name from the location
  const locationChips = Array.from(
    new Set(
      locations
        .slice(0, 5)
        .map((l) => getAccessLocationValue(l))
        .filter(Boolean)
    )
  ) as string[];
  const currentLocationChips = currentAccessLocationChips;

  return (
    <View style={styles.arDetailCard}>
      {/* Card title row */}
      <View style={styles.arDetailCardTitle}>
        <MaterialIcons name="description" size={16} color="#374151" />
        <Text style={styles.arDetailCardTitleText}>{cardTitle}</Text>
      </View>

      <View style={styles.arDetailBody}>
        {/* ?? KV Grid (varies by subcategory) ?? */}
        {isTemporary ? (
          <View style={styles.arKvGrid}>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>SUB-CATEGORY</Text>
              <Text style={styles.arKvValue}>{ticket.subcategory || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ACCESS TYPE</Text>
              <Text style={styles.arKvValue}>{ar.accessType || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ORGANIZATION</Text>
              <Text style={styles.arKvValue}>{ar.organization || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>DEPARTMENT</Text>
              <Text style={styles.arKvValue}>{ar.department || "-"}</Text>
            </View>
            {ar.endDate ? (
              <View style={styles.arKvItem}>
                <Text style={styles.arKvLabel}>ACCESS END DATE</Text>
                <Text style={styles.arKvValue}>{ar.endDate}</Text>
              </View>
            ) : null}
          </View>
        ) : isModify ? (
          <View style={styles.arKvGrid}>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ACCESS TYPE</Text>
              <Text style={styles.arKvValue}>{ar.accessType || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ORGANIZATION</Text>
              <Text style={styles.arKvValue}>{ar.organization || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>DEPARTMENT</Text>
              <Text style={styles.arKvValue}>{ar.department || "-"}</Text>
            </View>
          </View>
        ) : (
          /* New request */
          <View style={styles.arKvGrid}>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ACCESS TYPE</Text>
              <Text style={styles.arKvValue}>{ar.accessType || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ORGANIZATION</Text>
              <Text style={styles.arKvValue}>{ar.organization || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>DEPARTMENT</Text>
              <Text style={styles.arKvValue}>{ar.department || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>ROLE</Text>
              <Text style={styles.arKvValue}>{ar.role || "-"}</Text>
            </View>
            <View style={styles.arKvItem}>
              <Text style={styles.arKvLabel}>SCOPE</Text>
              <Text style={styles.arKvValue}>{scopeTierLabel}</Text>
            </View>
          </View>
        )}

        {/* ?? Before/After comparison for Modify & Temporary ?? */}
        {(isModify || isTemporary) && (
          <View style={styles.arCompareRow}>
            {/* Current Access */}
            <View style={styles.arCompareBox}>
              <View style={styles.arCompareLabelRow}>
                <Text style={styles.arCompareLabel}>CURRENT ACCESS</Text>
              </View>
              <Text style={styles.arCompareRoleName}>{ar.currentRole || "-"}</Text>
              <Text style={styles.arCompareScopeTier}>{currentScopeTierLabel}</Text>
              <View style={styles.arChipsRow}>
                {currentLocationChips.map((name) => (
                  <View key={name} style={[styles.arChip, styles.arChipCurrent]}>
                    <Text style={[styles.arChipText, styles.arChipTextCurrent]}>{name}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Arrow */}
            <View style={styles.arCompareArrow}>
              <MaterialIcons name="arrow-forward" size={20} color="#9ca3af" />
            </View>

            {/* Requested Change */}
            <View style={[styles.arCompareBox, styles.arCompareBoxRequested]}>
              <View style={styles.arCompareLabelRow}>
                <Text style={[styles.arCompareLabel, styles.arCompareLabelRequested]}>REQUESTED CHANGE</Text>
                {ar.currentRole && ar.currentRole !== ar.role ? (
                  <View style={styles.arRoleSwapBadge}>
                    <Text style={styles.arRoleSwapText}>ROLE SWAP</Text>
                  </View>
                ) : null}
              </View>
              <Text style={styles.arCompareRoleName}>{ar.role || "-"}</Text>
              <Text style={styles.arCompareScopeTier}>{scopeTierLabel}</Text>
              <View style={styles.arChipsRow}>
                {locationChips.map((name) => (
                  <View key={name} style={[styles.arChip, styles.arChipRequested]}>
                    <Text style={[styles.arChipText, styles.arChipTextRequested]}>{name}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* Temporary access info box */}
        {isTemporary && ar.endDate ? (
          <View style={styles.arInfoBox}>
            <MaterialIcons name="info-outline" size={16} color="#B45309" />
            <Text style={styles.arInfoBoxText}>
              {"Approving snapshots the current access and provisions the temporary role. On "}
              <Text style={{ fontWeight: "700" }}>{ar.endDate}</Text>
              {" the daily expiration job restores "}
              <Text style={{ fontWeight: "700" }}>{ar.currentRole || "original role"}</Text>
              {" with no Sustain action."}
            </Text>
          </View>
        ) : null}

        {/* Reusable role + location selector (same component/logic as Submit Ticket) */}
        {isEditing && !isReadOnly && (
          <View style={styles.arEditFormWrap}>
            <View style={styles.arEditFormDivider} />
            <Text style={styles.arEditFormTitle}>MODIFY ACCESS DETAILS</Text>
            <AccessRoleLocationSelector
              initialRole={ar.role || ""}
              initialOrganization={ar.organization || ""}
              initialDepartment={ar.department || ""}
              initialLocations={(ar.locations || []).map((l: any) => getAccessLocationValue(l)).filter(Boolean)}
              roleLabel="New Role"
              onChange={handleSelectorChange}
            />
          </View>
        )}

        <AccessibleModulesCard
          roleId={displayRoleId}
          roleName={displayRole}
          isFullScope={displayRoleScope?.type === "full"}
          endDate={isTemporary ? ar.endDate : null}
        />

        {/* ?? Read-only: Sites to grant (location table) ?? */}
        {!isEditing && locationTree.length > 0 && (
          <>
            <View style={styles.arSitesHeader}>
              <Text style={styles.arSitesTitle}>
                {firstLoc?.locationType === "REGION"
                  ? "Region to grant"
                  : firstLoc?.locationType === "MARKET"
                    ? "Markets to grant"
                    : "Sites to grant"}
              </Text>
              <Text style={styles.arSitesCount}>
                {countSelected > 0 ? `${countSelected} / ${totalSites} selected` : ""}
              </Text>
            </View>

            <Text style={styles.arSitesSubtitle}>{scopeGrantLabel}</Text>

            {/* Table */}
            <View style={styles.arSitesTable}>
              {/* Head */}
              <View style={styles.arSitesTableHead}>
                <Text style={[styles.arSitesTableHeadText, styles.arSitesTableColSite]}>
                  REGION - MARKET - SITE
                </Text>
                <Text style={[styles.arSitesTableHeadText, styles.arSitesTableColCoverage]}>
                  {firstLoc?.locationType === "REGION" ? "WHAT THIS GRANTS" : "COVERAGE"}
                </Text>
                <Text style={[styles.arSitesTableHeadText, { textAlign: "right" }]}>
                  STATUS
                </Text>
              </View>

              {/* Rows */}
              {/* This table only renders in the pre-Modify review state (!isEditing above),
                  so checkboxes must always be non-interactive here � actual editing happens
                  via the separate AccessRequestLocationPicker once Modify is clicked. */}
              {locationTree.map((node, idx) => (
                <SitesTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  expandedIds={expandedIds}
                  selectedIds={selectedIds}
                  onToggleExpand={onToggleExpand}
                  onToggleSelect={onToggleSelect}
                  isReadOnly={true}
                  isLast={idx === locationTree.length - 1}
                />
              ))}
            </View>

            {/* Hint */}
            {!isReadOnly && scopeLimit && requestedRoleScope?.type !== "full" ? (
              <View style={styles.arSitesHint}>
                <MaterialIcons name="info-outline" size={13} color="#9ca3af" />
                <Text style={styles.arSitesHintText}>
                  {`Select up to `}
                  <Text style={{ fontWeight: "700" }}>{scopeLimit} sites</Text>
                  {". Expand a region or market to drill into its sites - region- and market-level access isn't available to this role; it requires a Region or Market Leader role."}
                </Text>
              </View>
            ) : null}
            {!isReadOnly && firstLoc?.locationType === "REGION" ? (
              <View style={styles.arSitesHint}>
                <MaterialIcons name="info-outline" size={13} color="#9ca3af" />
                <Text style={styles.arSitesHintText}>
                  {"Pick "}
                  <Text style={{ fontWeight: "700" }}>one or more regions</Text>
                  {" - each grants every market and site inside it. Expand to preview what's included."}
                </Text>
              </View>
            ) : null}
          </>
        )}
      </View>
    </View>
  );
};
// ?? Access Request Skeleton (loading state) ???????????????????????????????????

/** Shimmer card shown while the Access Request ticket detail is loading from the API. */
export const AccessRequestSkeletonDetail: React.FC = () => {
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 800, useNativeDriver: true }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [pulse]);

  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.4, 0.85] });

  const Block = ({ width, height = 14, marginBottom = 10 }: { width: number | `${number}%`; height?: number; marginBottom?: number }) => (
    <Animated.View style={[styles.arSkeletonBlock, { width, height, marginBottom, opacity }]} />
  );

  return (
    <View style={styles.arDetailCard}>
      {/* Card title shimmer */}
      <View style={styles.arDetailCardTitle}>
        <Block width={16} height={16} marginBottom={0} />
        <Block width={220} height={14} marginBottom={0} />
      </View>

      <View style={styles.arDetailBody}>
        {/* KV grid rows */}
        <View style={styles.arKvGrid}>
          {[140, 160, 150, 170, 130].map((w, i) => (
            <View key={i} style={styles.arKvItem}>
              <Block width={80} height={10} />
              <Block width={w} height={14} />
            </View>
          ))}
        </View>

        {/* Sites header shimmer */}
        <View style={[styles.arSitesHeader, { marginTop: 16 }]}>
          <Block width={140} height={14} marginBottom={0} />
          <Block width={80} height={11} marginBottom={0} />
        </View>
        <Block width="60%" height={11} />

        {/* Table shimmer rows */}
        {[1, 2, 3].map((i) => (
          <View key={i} style={[styles.arSitesRow, { paddingVertical: 10 }]}>
            <Block width="50%" height={13} marginBottom={0} />
            <Block width={60} height={11} marginBottom={0} />
            <Block width={48} height={11} marginBottom={0} />
          </View>
        ))}
      </View>
    </View>
  );
};

// ?? Access Request Hero Skeleton (loading state) ??????????????????????????????

/**
 * Shimmer replacement for TicketHero while an Access Request ticket detail
 * is loading from the API. Mirrors the hero card structure (ID row, title,
 * submitter meta) without showing any generic non-AR content.
 */
export const AccessRequestHeroSkeleton: React.FC = () => {
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 800, useNativeDriver: true }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [pulse]);

  const opacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.4, 0.85] });

  const Block = ({ width, height = 14, marginBottom = 0 }: { width: number | `${number}%`; height?: number; marginBottom?: number }) => (
    <Animated.View style={[styles.arSkeletonBlock, { width, height, marginBottom, opacity }]} />
  );

  return (
    <View style={styles.detailHero}>
      {/* ID + badge row */}
      <View style={[styles.dhTop, { alignItems: "center" }]}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1 }}>
          <Block width={140} height={16} />
          <Block width={88} height={22} />
          <Block width={80} height={22} />
        </View>
        {/* Status badge placeholder (right side) */}
        <Block width={60} height={22} />
      </View>

      {/* Title shimmer */}
      <Block width="65%" height={22} marginBottom={8} />

      {/* Meta row shimmer */}
      <View style={{ flexDirection: "row", gap: 12, marginTop: 6 }}>
        <Block width={260} height={13} />
      </View>
    </View>
  );
};

// ?? Merged Tickets Section ???????????????????????????????????????????????????

export type AbsorbedTicketSummary = {
  id: string;
  title: string;
  /** ISO-8601 submission datetime */
  submitted: string;
  submittedBy?: string;
};

type MergedTicketsSectionProps = {
  absorbed: AbsorbedTicketSummary[];
};

export const MergedTicketsSection: React.FC<MergedTicketsSectionProps> = ({ absorbed }) => {
  const isDark = useTheme().resolvedTheme === "dark";
  if (!absorbed.length) return null;
  const mergedBadge = getStatusBadgeColors("closed-merged", isDark);

  return (
    <View style={styles.detailSection}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <MaterialIcons name="merge-type" size={16} color="#374151" />
          <Text style={styles.sectionTitle}>Merged Tickets</Text>
          <View style={styles.countPill}>
            <Text style={styles.countPillText}>{absorbed.length} absorbed</Text>
          </View>
        </View>
      </View>

      <View style={styles.sectionBody}>
        {absorbed.map((ticket, idx) => (
          <View
            key={ticket.id}
            style={[styles.mergedTicketRow, idx < absorbed.length - 1 && styles.mergedTicketRowBorder]}
          >
            <View style={styles.mergedTicketIconWrap}>
              <MaterialIcons name="call-merge" size={14} color="#7871E2" />
            </View>
            <View style={styles.mergedTicketInfo}>
              <View style={styles.mergedTicketIdRow}>
                <Text style={styles.mergedTicketId}>{ticket.id}</Text>
                <View style={[styles.badgeSm, { backgroundColor: mergedBadge.bg }]}>
                  <View style={[styles.badgeDotSm, { backgroundColor: mergedBadge.color }]} />
                  <Text style={[styles.badgeSmText, { color: mergedBadge.color }]} {...(isDark ? KEEP_COLOR_PROPS : {})}>Closed (Merged)</Text>
                </View>
              </View>
              <Text style={styles.mergedTicketTitle} numberOfLines={2}>{ticket.title}</Text>
              <View style={styles.mergedTicketMeta}>
                <Text style={styles.mergedTicketMetaText}>
                  {"Submitted: "}
                  <Text style={styles.mergedTicketMetaValue}>{formatDateWithTimezone(ticket.submitted)}</Text>
                </Text>
                {ticket.submittedBy ? (
                  <Text style={styles.mergedTicketMetaText}>
                    {"  -  By: "}
                    <Text style={styles.mergedTicketMetaValue}>{ticket.submittedBy}</Text>
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

// ?? Affected Locations Section ????????????????????????????????????????????????

type AffectedLocationsSectionProps = {
  ticket: TicketDetailRecord;
  locationsToShow: TicketDetailRecord["affectedLocations"];
  impactedPickersCount?: number;
  hasMoreLocations: boolean;
  showAll: boolean;
  onToggleShowAll: (v: boolean) => void;
  onAddLocation: (name: string, locId: string, sapId: string) => void;
  onRemoveLocation: (locationId: string) => void;
  /** When true, hides add/remove controls */
  isReadOnly?: boolean;
};



// ?? Add Scope type for the "add by" toggle ????????????????????????????????????
type AddScope = "Location" | "Market" | "Region";

export const AffectedLocationsSection: React.FC<AffectedLocationsSectionProps> = ({
  ticket,
  locationsToShow,
  impactedPickersCount = 0,
  hasMoreLocations,
  showAll,
  onToggleShowAll,
  onAddLocation,
  onRemoveLocation,
  isReadOnly,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  // Get all locations with access from Redux
  const allLocations = useSelector((state: any) => state.appData.locations) as Array<{
    id: string;
    name: string;
    locSapId?: string;
    market?: string;
    region?: string;
  }>;
  const distinctMarkets = useSelector(selectDistinctMarkets);
  const distinctRegions = useSelector(selectDistinctRegions);

  // Filter out locations already attached to this ticket
  const availableLocations = useMemo(() => {
    const attachedNames = new Set(ticket.affectedLocations.map((l) => l.name));
    return allLocations.filter((loc) => !attachedNames.has(loc.name));
  }, [allLocations, ticket.affectedLocations]);

  const [manageMode, setManageMode] = useState(false);
  const [addScope, setAddScope] = useState<AddScope>("Location");
  const [pickerOpen, setPickerOpen] = useState(false);
  // For Location scope
  const [selectedOption, setSelectedOption] = useState<{ name: string; locId: string; sapId: string } | null>(null);
  // For Market/Region scope
  const [selectedMarketRegion, setSelectedMarketRegion] = useState<string>("");
  const [marketRegionPickerOpen, setMarketRegionPickerOpen] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [hoveredRemoveId, setHoveredRemoveId] = useState<string | null>(null);
  const [pendingRemoveLocation, setPendingRemoveLocation] = useState<{ id: string; name: string } | null>(null);
  const [pendingBulkAdd, setPendingBulkAdd] = useState<{
    scope: "Market" | "Region";
    value: string;
    locations: Array<{ name: string; locId: string; sapId: string }>;
  } | null>(null);

  // Reset picker state when scope changes or manage mode closes
  const resetPickerState = () => {
    setPickerOpen(false);
    setSelectedOption(null);
    setSelectedMarketRegion("");
    setMarketRegionPickerOpen(false);
  };

  // Locations already under the selected market/region (to show preview count)
  const locationsUnderSelection = useMemo(() => {
    if (addScope === "Market" && selectedMarketRegion) {
      return availableLocations.filter(
        (loc) => (loc.market ?? "").trim().toLowerCase() === selectedMarketRegion.trim().toLowerCase()
      );
    }
    if (addScope === "Region" && selectedMarketRegion) {
      return availableLocations.filter(
        (loc) => (loc.region ?? "").trim().toLowerCase() === selectedMarketRegion.trim().toLowerCase()
      );
    }
    return [];
  }, [addScope, selectedMarketRegion, availableLocations]);

  const handleAddBulk = () => {
    if (!selectedMarketRegion || locationsUnderSelection.length === 0) return;
    const locsToAdd = locationsUnderSelection.map((loc) => ({
      name: loc.name,
      locId: String(loc.id),
      sapId: loc.locSapId || "",
    }));
    setPendingBulkAdd({
      scope: addScope as "Market" | "Region",
      value: selectedMarketRegion,
      locations: locsToAdd,
    });
  };

  const confirmBulkAdd = () => {
    if (!pendingBulkAdd) return;
    pendingBulkAdd.locations.forEach((loc) => {
      onAddLocation(loc.name, loc.locId, loc.sapId);
    });
    setSelectedMarketRegion("");
    setMarketRegionPickerOpen(false);
    setPendingBulkAdd(null);
  };

  const marketRegionOptions = addScope === "Market" ? distinctMarkets : distinctRegions;

  return (
    <View style={styles.detailSection}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <MaterialIcons name="location-on" size={16} color="#374151" />
          <Text style={styles.sectionTitle}>Affected Locations</Text>
          <View style={styles.countPill}>
            <Text style={styles.countPillText}>{ticket.affectedLocations.length} sites</Text>
          </View>
        </View>
        <View style={styles.locationHeaderActions}>
          {!isReadOnly && (!manageMode ? (
            <Pressable
              style={({ pressed }: { pressed: boolean }) => [styles.manageLocationsBtn, pressed && styles.btnPressed]}
              onPress={() => { setManageMode(true); resetPickerState(); setAddScope("Location"); }}
            >
              <MaterialIcons name="add" size={14} color="#2D2D2D" />
              <Text style={styles.manageLocationsBtnText}>Manage Locations</Text>
            </Pressable>
          ) : (
            <Pressable
              style={({ pressed }: { pressed: boolean }) => [styles.doneBtn, pressed && styles.btnPressed]}
              onPress={() => { setManageMode(false); resetPickerState(); }}
            >
              <Text style={styles.doneBtnText}>Done</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.sectionBody}>
        {!manageMode ? (
          <>
            <View style={styles.impactSummary}>
              <Text style={styles.impactBigNum}>{ticket.affectedLocations.length}</Text>
              <View>
                <Text style={styles.impactTitle}>locations reporting this issue</Text>
                <Text style={styles.impactSubtitle}>
                  {`${ticket.totalImpactedPickers ?? impactedPickersCount} user${(ticket.totalImpactedPickers ?? impactedPickersCount) === 1 ? "" : "s"} impacted`}
                </Text>
              </View>
            </View>

            <View style={styles.locationGrid}>
              {locationsToShow.map((loc) => (
                <View key={loc.id} style={[styles.locationRow, loc.isSubmitter ? styles.locationRowSubmitter : null]}>
                  <Text style={[styles.locationName, loc.isSubmitter ? styles.locationNameSubmitter : null]}>
                    {loc.name}
                    <Text style={styles.locationSapId}> SAP: {loc.sapId}</Text>
                    {loc.isSubmitter ? <Text style={styles.locationSubmitterTag}> (submitter)</Text> : null}
                  </Text>
                  <Text style={styles.locationTime}>{loc.time}</Text>
                </View>
              ))}
            </View>

            {hasMoreLocations && (
              <Pressable
                style={({ pressed }: { pressed: boolean }) => [styles.btnGhostSm, pressed && styles.btnPressed, styles.showMoreBtn]}
                onPress={() => onToggleShowAll(!showAll)}
              >
                <Text style={styles.btnGhostSmText}>
                  {showAll ? "Show less" : `Show ${ticket.affectedLocations.length - locationsToShow.length} more`}
                </Text>
                <MaterialIcons
                  name={showAll ? "expand-less" : "expand-more"}
                  size={14}
                  color="#374151"
                />
              </Pressable>
            )}
          </>
        ) : (
          <>
            {/* ?? Add Section ??????????????????????????????????????????? */}
            <View style={[
              styles.locationFieldWrap,
              {
                zIndex: (pickerOpen || marketRegionPickerOpen) ? 100 : 10,
                // On web, allow the dropdown to overflow outside this container
                ...(Platform.OS === "web" ? { overflow: "visible" } as any : {}),
              },
            ]}>
              {/* Scope toggle: Location / Market / Region */}
              <Text style={styles.locationFieldLabel}>Add by</Text>
              <View style={{ flexDirection: "row", gap: 8, marginBottom: 12 }}>
                {(["Location", "Market", "Region"] as AddScope[]).map((scope) => (
                  <Pressable
                    key={scope}
                    onPress={() => { setAddScope(scope); resetPickerState(); }}
                    style={({ pressed }) => [{
                      paddingHorizontal: 14,
                      paddingVertical: 7,
                      borderRadius: 20,
                      borderWidth: 1.5,
                      borderColor: addScope === scope
                        ? (isDark ? "#6AADFF" : "#034895")
                        : (isDark ? "#5A5A5A" : "#d1d5db"),
                      backgroundColor: addScope === scope
                        ? (isDark ? "#1B2A3D" : "#EAF0FB")
                        : (isDark ? "#2B2B2B" : "#ffffff"),
                      opacity: pressed ? 0.8 : 1,
                    }]}
                  >
                    <Text
                      {...(isDark ? KEEP_COLOR_PROPS : {})}
                      style={{
                        fontSize: 13,
                        fontWeight: addScope === scope ? "700" : "500",
                        color: addScope === scope
                          ? (isDark ? "#6AADFF" : "#034895")
                          : (isDark ? "#FFFFFF" : "#374151"),
                      }}
                    >
                      {scope}
                    </Text>
                  </Pressable>
                ))}
              </View>

              {/* ?? Location picker ?? */}
              {addScope === "Location" && (
                <View
                  style={[
                    styles.locationAddRow,
                    { zIndex: pickerOpen ? 50 : 1 },
                    Platform.OS === "web" ? { overflow: "visible" } as any : null,
                  ]}
                >
                  <View style={[
                    styles.locationPickerWrap,
                    Platform.OS === "web" ? { overflow: "visible" } as any : null,
                  ]}>
                    <Pressable
                      onPress={() => setPickerOpen((prev) => !prev)}
                      style={({ pressed }: { pressed: boolean }) => [styles.locationPickerTrigger, pressed && styles.btnPressed]}
                    >
                      <Text style={[styles.locationPickerText, !selectedOption && styles.locationPickerPlaceholder]}>
                        {selectedOption ? `${selectedOption.name} (SAP: ${selectedOption.sapId})` : "Select a location to add..."}
                      </Text>
                      <MaterialIcons name={pickerOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={16} color="#585858" />
                    </Pressable>

                    {pickerOpen ? (
                      <View style={[
                        styles.locationPickerMenu,
                        Platform.OS === "web" ? {
                          position: "absolute" as const,
                          top: "100%" as any,
                          left: 0,
                          right: 0,
                          zIndex: 9999,
                        } : null,
                      ]}>
                        <View style={styles.locationPickerMenuHeader}>
                          <Text style={styles.locationPickerMenuHeaderText}>Select a location to add...</Text>
                        </View>
                        {availableLocations.length === 0 ? (
                          <Text style={{ padding: 12, color: "#888" }}>No locations available</Text>
                        ) : (
                          availableLocations.map((opt) => (
                            <Pressable
                              key={opt.id}
                              onPress={() => {
                                setSelectedOption({ name: opt.name, locId: String(opt.id), sapId: opt.locSapId || "" });
                                setPickerOpen(false);
                              }}
                              style={({ pressed }: { pressed: boolean }) => [
                                styles.locationPickerItem,
                                pressed ? styles.btnPressed : null,
                              ]}
                            >
                              <Text style={styles.locationPickerItemText}>
                                {opt.name} {opt.locSapId ? `(SAP: ${opt.locSapId})` : ""}
                              </Text>
                            </Pressable>
                          ))
                        )}
                      </View>
                    ) : null}
                  </View>

                  <Pressable
                    onPress={() => {
                      if (!selectedOption) return;
                      onAddLocation(selectedOption.name, selectedOption.locId, selectedOption.sapId);
                      setSelectedOption(null);
                      setPickerOpen(false);
                    }}
                    disabled={!selectedOption}
                    style={({ pressed }: { pressed: boolean }) => [
                      styles.locationAddBtn,
                      !selectedOption ? styles.locationAddBtnDisabled : null,
                      pressed && selectedOption ? styles.btnPressed : null,
                    ]}
                  >
                    <MaterialIcons name="add" size={14} color={selectedOption ? "#ffffff" : "#818181"} />
                    <Text style={[styles.locationAddBtnText, !selectedOption ? styles.locationAddBtnTextDisabled : null]}>Add</Text>
                  </Pressable>
                </View>
              )}

              {/* ?? Market / Region picker ?? */}
              {(addScope === "Market" || addScope === "Region") && (
                <View style={Platform.OS === "web" ? { overflow: "visible" } as any : null}>
                  <View
                    style={[
                      styles.locationAddRow,
                      { zIndex: marketRegionPickerOpen ? 50 : 1 },
                      Platform.OS === "web" ? { overflow: "visible" } as any : null,
                    ]}
                  >
                    <View style={[
                      styles.locationPickerWrap,
                      Platform.OS === "web" ? { overflow: "visible" } as any : null,
                    ]}>
                      <Pressable
                        onPress={() => setMarketRegionPickerOpen((prev) => !prev)}
                        style={({ pressed }: { pressed: boolean }) => [styles.locationPickerTrigger, pressed && styles.btnPressed]}
                      >
                        <Text style={[styles.locationPickerText, !selectedMarketRegion && styles.locationPickerPlaceholder]}>
                          {selectedMarketRegion || `Select a ${addScope.toLowerCase()} to add...`}
                        </Text>
                        <MaterialIcons name={marketRegionPickerOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={16} color="#585858" />
                      </Pressable>

                      {marketRegionPickerOpen ? (
                        <View style={[
                          styles.locationPickerMenu,
                          Platform.OS === "web" ? {
                            position: "absolute" as const,
                            top: "100%" as any,
                            left: 0,
                            right: 0,
                            zIndex: 9999,
                          } : null,
                        ]}>
                          <View style={styles.locationPickerMenuHeader}>
                            <Text style={styles.locationPickerMenuHeaderText}>
                              Select a {addScope.toLowerCase()}...
                            </Text>
                          </View>
                          {marketRegionOptions.length === 0 ? (
                            <Text style={{ padding: 12, color: "#888" }}>No {addScope.toLowerCase()}s available</Text>
                          ) : (
                            marketRegionOptions.map((opt) => (
                              <Pressable
                                key={opt}
                                onPress={() => {
                                  setSelectedMarketRegion(opt);
                                  setMarketRegionPickerOpen(false);
                                }}
                                style={({ pressed }: { pressed: boolean }) => [
                                  styles.locationPickerItem,
                                  pressed ? styles.btnPressed : null,
                                ]}
                              >
                                <Text style={styles.locationPickerItemText}>{opt}</Text>
                              </Pressable>
                            ))
                          )}
                        </View>
                      ) : null}
                    </View>

                    <Pressable
                      onPress={handleAddBulk}
                      disabled={!selectedMarketRegion || locationsUnderSelection.length === 0}
                      style={({ pressed }: { pressed: boolean }) => [
                        styles.locationAddBtn,
                        (!selectedMarketRegion || locationsUnderSelection.length === 0) ? styles.locationAddBtnDisabled : null,
                        pressed && selectedMarketRegion && locationsUnderSelection.length > 0 ? styles.btnPressed : null,
                      ]}
                    >
                      <MaterialIcons
                        name="add"
                        size={14}
                        color={selectedMarketRegion && locationsUnderSelection.length > 0 ? "#ffffff" : "#818181"}
                      />
                      <Text style={[
                        styles.locationAddBtnText,
                        (!selectedMarketRegion || locationsUnderSelection.length === 0) ? styles.locationAddBtnTextDisabled : null,
                      ]}>
                        Add All
                      </Text>
                    </Pressable>
                  </View>

                  {/* Preview: how many locations will be added */}
                  {selectedMarketRegion ? (
                    <View style={{
                      marginTop: 8,
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      backgroundColor: locationsUnderSelection.length > 0 ? "#EAF0FB" : "#fef3c7",
                      borderRadius: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                    }}>
                      <MaterialIcons
                        name={locationsUnderSelection.length > 0 ? "info-outline" : "warning"}
                        size={14}
                        color={locationsUnderSelection.length > 0 ? "#034895" : "#d97706"}
                      />
                      <Text style={{
                        fontSize: 13,
                        color: locationsUnderSelection.length > 0 ? "#034895" : "#92400e",
                        flex: 1,
                      }}>
                        {locationsUnderSelection.length > 0
                          ? `${locationsUnderSelection.length} location${locationsUnderSelection.length === 1 ? "" : "s"} under "${selectedMarketRegion}" will be added.`
                          : `No new locations found under "${selectedMarketRegion}" - all may already be attached.`}
                      </Text>
                    </View>
                  ) : null}
                </View>
              )}
            </View>

            {/* ?? Current locations list ??????????????????????????????? */}
            <View style={[styles.locationFieldWrap, { zIndex: 1 }]}>
              <Text style={styles.locationFieldLabel}>Current locations</Text>
              <View style={styles.locationGrid}>
                {ticket.affectedLocations.map((loc) => (
                  <View
                    key={loc.id}
                    // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
                    onMouseEnter={() => setHoveredCardId(loc.id)}
                    onMouseLeave={() => setHoveredCardId(null)}
                    style={[
                      styles.locationCard,
                      loc.isSubmitter ? styles.locationCardSubmitter : null,
                      hoveredCardId === loc.id && !loc.isSubmitter ? styles.locationCardHovered : null,
                    ]}
                  >
                    <Text style={[styles.locationCardName, loc.isSubmitter ? styles.locationCardNameSubmitter : null]}>
                      {loc.name}
                      {loc.isSubmitter ? <Text style={styles.locationCardSubmitterText}> (submitter, cannot remove)</Text> : null}
                      <Text style={styles.locationCardTime}>{"  "}{loc.time}</Text>
                    </Text>
                    <Pressable
                      disabled={!!loc.isSubmitter}
                      onPress={() => !loc.isSubmitter && setPendingRemoveLocation({ id: loc.id, name: loc.name })}
                      // @ts-ignore � onMouseEnter/onMouseLeave are valid on React Native Web
                      onMouseEnter={() => !loc.isSubmitter && setHoveredRemoveId(loc.id)}
                      onMouseLeave={() => setHoveredRemoveId(null)}
                      style={[
                        styles.locationRemoveBtn,
                        loc.isSubmitter ? styles.locationRemoveBtnDisabled : null,
                        hoveredRemoveId === loc.id && !loc.isSubmitter ? styles.locationRemoveBtnHover : null,
                      ]}
                    >
                      <MaterialIcons
                        name="delete-outline"
                        size={16}
                        color={
                          loc.isSubmitter
                            ? "#bdbdbd"
                            : hoveredRemoveId === loc.id
                            ? "#C12C01"
                            : "#656565"
                        }
                      />
                    </Pressable>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}
      </View>

      {/* Remove Location Confirmation Dialog */}
      <Modal
        visible={!!pendingRemoveLocation}
        transparent
        animationType="fade"
        onRequestClose={() => setPendingRemoveLocation(null)}
      >
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.25)", justifyContent: "center", alignItems: "center" }}>
          <View style={{ backgroundColor: "#fff", borderRadius: 10, padding: 24, minWidth: 320, maxWidth: "90%", alignItems: "center", elevation: 4 }}>
            <MaterialIcons name="delete-outline" size={32} color="#C12C01" style={{ marginBottom: 8 }} />
            <Text style={{ fontSize: 18, fontWeight: "bold", marginBottom: 8, textAlign: "center" }}>
              Remove {pendingRemoveLocation?.name} from this ticket?
            </Text>
            <Text style={{ fontSize: 15, color: "#585858", marginBottom: 20, textAlign: "center" }}>
              This will not delete any data.
            </Text>
            <View style={{ flexDirection: "row", gap: 12 }}>
              <Pressable
                style={({ pressed }) => [{
                  backgroundColor: "#C12C01",
                  paddingVertical: 10,
                  paddingHorizontal: 24,
                  borderRadius: 6,
                  marginRight: 8,
                  opacity: pressed ? 0.85 : 1,
                }]}
                onPress={() => {
                  if (pendingRemoveLocation) onRemoveLocation(pendingRemoveLocation.id);
                  setPendingRemoveLocation(null);
                }}
              >
                <Text style={{ color: "#fff", fontWeight: "bold", fontSize: 15 }}>Remove</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [{
                  backgroundColor: "#E9E9E9",
                  paddingVertical: 10,
                  paddingHorizontal: 24,
                  borderRadius: 6,
                  opacity: pressed ? 0.85 : 1,
                }]}
                onPress={() => setPendingRemoveLocation(null)}
              >
                <Text style={{ color: "#2D2D2D", fontWeight: "bold", fontSize: 15 }}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Bulk Add Confirmation Dialog (Market / Region) */}
      <Modal
        visible={!!pendingBulkAdd}
        transparent
        animationType="fade"
        onRequestClose={() => setPendingBulkAdd(null)}
      >
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.25)", justifyContent: "center", alignItems: "center" }}>
          <View style={{ backgroundColor: "#fff", borderRadius: 10, padding: 24, minWidth: 320, maxWidth: "90%", elevation: 4 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <MaterialIcons name="add-location-alt" size={28} color="#034895" />
              <Text style={{ fontSize: 17, fontWeight: "bold", color: "#111827", flex: 1 }}>
                Add {pendingBulkAdd?.locations.length} location{(pendingBulkAdd?.locations.length ?? 0) !== 1 ? "s" : ""}?
              </Text>
            </View>
            <Text style={{ fontSize: 14, color: "#374151", marginBottom: 6 }}>
              All locations under {pendingBulkAdd?.scope}{" "}
              <Text style={{ fontWeight: "700" }}>"{pendingBulkAdd?.value}"</Text>{" "}
              will be added to this ticket's affected locations.
            </Text>
            {(pendingBulkAdd?.locations ?? []).length > 0 && (
              <ScrollView style={{ maxHeight: 140, marginBottom: 16 }}>
                {(pendingBulkAdd?.locations ?? []).map((loc) => (
                  <Text key={loc.locId} style={{ fontSize: 13, color: "#6b7280", paddingVertical: 2 }}>
                    - {loc.name}{loc.sapId ? ` (SAP: ${loc.sapId})` : ""}
                  </Text>
                ))}
              </ScrollView>
            )}
            <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end" }}>
              <Pressable
                style={({ pressed }) => [{
                  borderWidth: 1,
                  borderColor: "#d1d5db",
                  paddingVertical: 10,
                  paddingHorizontal: 20,
                  borderRadius: 6,
                  opacity: pressed ? 0.8 : 1,
                }]}
                onPress={() => setPendingBulkAdd(null)}
              >
                <Text style={{ color: "#374151", fontWeight: "600", fontSize: 14 }}>Cancel</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [{
                  backgroundColor: "#034895",
                  paddingVertical: 10,
                  paddingHorizontal: 20,
                  borderRadius: 6,
                  opacity: pressed ? 0.85 : 1,
                }]}
                onPress={confirmBulkAdd}
              >
                <Text style={{ color: "#fff", fontWeight: "700", fontSize: 14 }}>
                  Add {pendingBulkAdd?.locations.length} Location{(pendingBulkAdd?.locations.length ?? 0) !== 1 ? "s" : ""}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ?? Shared composer constants ?????????????????????????????????????????????????
const MAX_COMMENT_CHARS = 2000;
const MAX_ATTACH_FILES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Supported file extensions for upload */
const ALLOWED_FILE_EXTENSIONS = [".jpg", ".jpeg", ".heic", ".png", ".pdf", ".xlsx", ".docx"];

/** MIME types for image files (used to detect image vs. document for preview) */
const IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const IMAGE_FILE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".HEIC", ".heic"];

/**
 * Returns the file extension from a filename string (with leading dot, lowercase).
 * Returns "" if no extension found.
 */
function getFileExtension(fileName: string): string {
  const name = String(fileName || "");
  const dotIdx = name.lastIndexOf(".");
  if (dotIdx < 0) return "";
  return name.slice(dotIdx).toLowerCase();
}

/**
 * Returns a Material icon name appropriate for the given file extension.
 */
function getFileIcon(ext: string): string {
  switch (ext) {
    case ".pdf": return "picture-as-pdf";
    case ".xlsx": return "table-chart";
    case ".docx": return "description";
    case ".jpg":
    case ".jpeg":
    case ".heic":
    case ".png": return "image";
    default: return "insert-drive-file";
  }
}

/**
 * Returns a background color for the file extension badge.
 */
function getFileExtColor(ext: string): string {
  switch (ext) {
    case ".pdf": return "#ef4444";
    case ".xlsx": return "#16a34a";
    case ".docx": return "#2563eb";
    case ".jpg":
    case ".jpeg":
    case ".heic":
    case ".png": return "#7c3aed";
    default: return "#6b7280";
  }
}

/**
 * Returns true when an error indicates the file was too large.
 */
function isFileTooLargeError(error: unknown): boolean {
  if (!error) return false;
  const status = (error as any)?.response?.status;
  if (status === 413) return true;
  const msg = String(
    (error as any)?.response?.data?.message ||
    (error as any)?.message ||
    ""
  ).toLowerCase();
  return (
    msg.includes("too large") ||
    msg.includes("file size") ||
    msg.includes("exceeds") ||
    msg.includes("payload") ||
    msg.includes("limit")
  );
}

type AttachedFile = {
  id: string;
  uri: string;
  originalName: string;
  extension: string;
  uploadedFileName?: string;
  fileUrl?: string;
  isUploading?: boolean;
  isImage?: boolean;
};

// Keep legacy type alias for any remaining usages
type AttachedImage = AttachedFile;

// ?? File attachment thumbnail (display existing attachments from API) ?????????

/**
 * Extracts the file extension from a URL or filename.
 * Returns the extension with leading dot in lowercase (e.g. ".pdf"), or "" if unknown.
 */
function getFileExtFromUrl(url: string): string {
  if (!url) return "";
  // Strip query string, then extract extension from the path
  const path = url.split("?")[0];
  const dotIdx = path.lastIndexOf(".");
  if (dotIdx < 0) return "";
  return path.slice(dotIdx).toLowerCase();
}

function isImageExtension(ext: string): boolean {
  return IMAGE_FILE_EXTENSIONS.includes(ext.toLowerCase());
}

/**
 * Extracts the filename from a URL (last path segment after stripping query string).
 */
function getFileNameFromUrl(url: string): string {
  if (!url) return "file";
  try {
    const path = url.split("?")[0];
    const parts = path.split("/");
    const name = decodeURIComponent(parts[parts.length - 1] || "file");
    return name || "file";
  } catch {
    return "file";
  }
}

const AttachmentThumbnail: React.FC<{ url: string; onImagePress?: (url: string) => void }> = ({ url, onImagePress }) => {
  const ext = getFileExtFromUrl(url);
  const isImage = isImageExtension(ext);
  const iconName = getFileIcon(ext) as any;
  const iconColor = getFileExtColor(ext);
  const displayName = getFileNameFromUrl(url);
  const extLabel = ext ? ext.replace(".", "").toUpperCase() : (isImage ? "IMG" : "FILE");

  const handleOpen = () => {
    if (isImage && onImagePress) {
      onImagePress(url);
      return;
    }
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      Linking.openURL(url);
    }
  };

  return (
    <Pressable
      onPress={handleOpen}
      style={({ pressed }) => [{
        width: isImage ? 104 : 72,
        alignItems: "center",
        marginRight: 10,
        marginBottom: 6,
        opacity: pressed ? 0.75 : 1,
        ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
      }]}
    >
      {/* Image preview for images; file icon for non-image files */}
      <View style={{
        width: isImage ? 104 : 64,
        height: isImage ? 78 : 64,
        borderRadius: 10,
        backgroundColor: isImage ? "#eef2ff" : iconColor,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 4,
        shadowOffset: { width: 0, height: 2 },
        elevation: 2,
        marginBottom: 5,
        overflow: "hidden" as const,
      }}>
        {isImage ? (
          <Image source={{ uri: url }} style={{ width: "100%", height: "100%" }} resizeMode="cover" />
        ) : (
          <MaterialIcons name={iconName} size={28} color="#ffffff" />
        )}
        {/* Extension badge */}
        <View style={{
          position: "absolute",
          bottom: 4,
          backgroundColor: "rgba(0,0,0,0.32)",
          borderRadius: 3,
          paddingHorizontal: 4,
          paddingVertical: 1,
        }}>
          <Text style={{ fontSize: 8, color: "#ffffff", fontWeight: "700", letterSpacing: 0.4 }}>
            {extLabel}
          </Text>
        </View>
      </View>
      {/* Filename below icon for non-image attachments only */}
      {!isImage ? (
        <Text
          style={{ fontSize: 10, color: "#374151", textAlign: "center", maxWidth: 72 }}
          numberOfLines={2}
        >
          {displayName}
        </Text>
      ) : null}
    </Pressable>
  );
};

// ?? File chip component ????????????????????????????????????????????????????????

const FileChip: React.FC<{
  file: AttachedFile;
  onRemove: (id: string) => void;
  onImagePress?: (url: string) => void;
}> = ({ file, onRemove, onImagePress }) => {
  const ext = file.extension || ".file";
  const isImage = Boolean(file.isImage) || isImageExtension(ext);
  const iconName = getFileIcon(ext) as any;
  const iconColor = getFileExtColor(ext);
  const openUploadedFile = () => {
    if (!file.fileUrl || file.isUploading) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(file.fileUrl, "_blank", "noopener,noreferrer");
    } else {
      Linking.openURL(file.fileUrl);
    }
  };

  const iconOrPreview = file.isUploading ? (
    <MaterialIcons name="cloud-upload" size={18} color="#9ca3af" />
  ) : isImage ? (
    <Image
      source={{ uri: file.fileUrl || file.uri }}
      style={{ width: 40, height: 56, borderRadius: 6 }}
      resizeMode="cover"
    />
  ) : (
    <View style={{
      width: 28,
      height: 28,
      borderRadius: 6,
      backgroundColor: iconColor,
      alignItems: "center",
      justifyContent: "center",
    }}>
      <MaterialIcons name={iconName} size={16} color="#ffffff" />
    </View>
  );

  const handleImageOpen = () => {
    const src = file.fileUrl || file.uri;
    if (!src || file.isUploading) return;
    if (onImagePress) {
      onImagePress(src);
      return;
    }
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(src, "_blank", "noopener,noreferrer");
    } else {
      Linking.openURL(src);
    }
  };

  return (
    <Pressable
      onPress={isImage ? handleImageOpen : ((!file.isUploading && file.fileUrl) ? openUploadedFile : undefined)}
      style={({ pressed }) => [{
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: "#f1f5f9",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "#e2e8f0",
      paddingHorizontal: 10,
      paddingVertical: 6,
      gap: 6,
      marginRight: 8,
      marginBottom: 4,
      }, ((isImage || (!isImage && !file.isUploading && file.fileUrl)) && pressed) ? { opacity: 0.85 } : null]}
    >
      {iconOrPreview}
      {!isImage ? (
        <View style={{ flexShrink: 1, maxWidth: 100 }}>
          <Text style={{ fontSize: 11, color: "#374151", fontWeight: "600" }} numberOfLines={1}>
            {file.originalName || `file${ext}`}
          </Text>
          {file.isUploading ? (
            <Text style={{ fontSize: 10, color: "#9ca3af" }}>Uploading...</Text>
          ) : file.fileUrl ? (
            <Text style={{ fontSize: 10, color: "#2563eb", fontWeight: "700" }}>
              OPEN
            </Text>
          ) : (
            <Text style={{ fontSize: 10, color: iconColor, fontWeight: "700", textTransform: "uppercase" }}>
              {ext.replace(".", "")}
            </Text>
          )}
        </View>
      ) : null}
      <Pressable
        onPress={() => onRemove(file.id)}
        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
        style={{
          width: isImage ? 16 : 18,
          height: isImage ? 16 : 18,
          borderRadius: isImage ? 8 : 9,
          backgroundColor: isImage ? "#1f2937" : "#94a3b8",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: isImage ? -6 : 2,
          marginTop: isImage ? -42 : 0,
        }}
      >
        <MaterialIcons name="close" size={9} color="#ffffff" />
      </Pressable>
    </Pressable>
  );
};

// ?? Notes Section ?????????????????????????????????????????????????????????????

type NotesSectionProps = {
  notes: NoteItem[];
  currentUserName: string;
  currentUserInitials: string;
  currentUserAvatarColor: string;
  onSubmit: (text: string, images: string[]) => Promise<boolean>;
  /** When true, hides the note input form */
  isReadOnly?: boolean;
  hideSectionHeader?: boolean;
};

export const NotesSection: React.FC<NotesSectionProps> = ({
  notes,
  currentUserName,
  currentUserInitials,
  currentUserAvatarColor,
  onSubmit,
  isReadOnly,
  hideSectionHeader,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  const [text, setText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [fileError, setFileError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lightboxUri, setLightboxUri] = useState<string | null>(null);

  const canSubmit = text.trim().length > 0 || attachedFiles.length > 0;

  /** Process picked files (shared by web input and native picker) */
  const processPickedFiles = useCallback(async (pickedFiles: Array<{
    name: string; uri: string; mimeType: string; size: number; base64?: string;
  }>) => {
    const newFiles: AttachedFile[] = [];
    let err = "";
    for (const picked of pickedFiles) {
      const ext = getFileExtension(picked.name);
      if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
        err = "File type not supported. Please upload .jpg, .jpeg, .heic, .png, .pdf, .xlsx, or .docx. files only.";
        continue;
      }
      if (picked.size > MAX_FILE_BYTES) {
        err = "File exceeds 10MB limit. Please select a smaller file.";
        continue;
      }
      if (attachedFiles.length + newFiles.length >= MAX_ATTACH_FILES) {
        err = `You can attach up to ${MAX_ATTACH_FILES} files per comment.`;
        break;
      }
      const isImg = IMAGE_MIME_TYPES.includes(picked.mimeType) || [".jpg", ".jpeg", ".heic", ".png"].includes(ext);
      newFiles.push({
        id: `nf-${Date.now()}-${newFiles.length}`,
        uri: picked.uri,
        originalName: picked.name,
        extension: ext,
        isUploading: true,
        isImage: isImg,
      });
    }
    if (err) setFileError(err);
    if (newFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newFiles]);
      const api = createApiClient();
      for (const f of newFiles) {
        try {
          const result = await uploadTicketFile(api, f.uri, f.originalName, "ticket-note");
          if (result) {
            setAttachedFiles((prev) =>
              prev.map((p) =>
                p.id === f.id ? { ...p, uploadedFileName: result.fileName, fileUrl: result.fileUrl, isUploading: false } : p
              )
            );
          } else {
            setAttachedFiles((prev) => prev.filter((p) => p.id !== f.id));
            setFileError("File upload failed. Please try again.");
          }
        } catch (uploadErr) {
          setAttachedFiles((prev) => prev.filter((p) => p.id !== f.id));
          setFileError(isFileTooLargeError(uploadErr) ? "File exceeds 10MB limit. Please select a smaller file." : "File upload failed. Please try again.");
        }
      }
    }
  }, [attachedFiles.length]);

  const pickFile = useCallback(() => {
    setFileError("");
    if (attachedFiles.length >= MAX_ATTACH_FILES) {
      setFileError(`You can attach up to ${MAX_ATTACH_FILES} files per comment.`);
      return;
    }
    if (Platform.OS === "web") {
      // On web, use a hidden <input type="file"> for full file type support
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".jpg,.jpeg,.heic,.png,.pdf,.xlsx,.docx";
      input.multiple = true;
      input.onchange = async (e: any) => {
        const files: FileList = e.target.files;
        if (!files || files.length === 0) return;
        const picked: Array<{ name: string; uri: string; mimeType: string; size: number }> = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const uri = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          picked.push({ name: file.name, uri, mimeType: file.type, size: file.size });
        }
        processPickedFiles(picked);
      };
      input.click();
    } else {
      launchImageLibrary(
        { mediaType: "mixed", quality: 1, selectionLimit: MAX_ATTACH_FILES - attachedFiles.length, includeBase64: true },
        async (response) => {
          if (response.didCancel || !response.assets) return;
          const picked = response.assets.map((asset) => {
            const rawFileName = String(asset.fileName || asset.uri?.split("/").pop() || "file");
            const mimeType = (asset.type || "").toLowerCase();
            const resolvedMime = mimeType || "application/octet-stream";
            const uri = asset.base64 ? `data:${resolvedMime};base64,${asset.base64}` : String(asset.uri || "");
            const size = asset.base64 ? Math.ceil(asset.base64.length * 0.75) : (asset.fileSize ?? 0);
            return { name: rawFileName, uri, mimeType: resolvedMime, size };
          });
          processPickedFiles(picked);
        }
      );
    }
  }, [attachedFiles.length, processPickedFiles]);

  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
    setFileError("");
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError("");
    if (!canSubmit) {
      setSubmitError("Note cannot be empty.");
      return;
    }
    // ?? URL validation (Requirement 3): check before API call ?????????????
    if (text.trim()) {
      const urlError = validateTextUrls(text);
      if (urlError) {
        setSubmitError(urlError);
        return;
      }
    }
    const stillUploading = attachedFiles.some((f) => f.isUploading);
    if (stillUploading) {
      setSubmitError("Please wait for files to finish uploading.");
      return;
    }
    setIsSubmitting(true);
    try {
      const fileNames = attachedFiles
        .map((f) => f.uploadedFileName)
        .filter((n): n is string => typeof n === "string" && n.length > 0);
      const ok = await onSubmit(text, fileNames);
      if (ok) {
        setText("");
        setAttachedFiles([]);
        setFileError("");
      } else {
        setSubmitError("Unable to submit your note. Please try again.");
      }
    } catch {
      setSubmitError("Unable to submit your note. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, text, attachedFiles, onSubmit]);

  return (
    <View style={styles.detailSection}>
      {!hideSectionHeader && (
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <MaterialIcons name="notes" size={16} color="#374151" />
          <Text style={styles.sectionTitle}>Notes</Text>
          <Text style={styles.sectionSubtitle}>Internal working notes, not visible to submitters</Text>
        </View>
      </View>
      )}

      <View style={styles.sectionBody}>
        {/* Composer � hidden when read-only */}
        {!isReadOnly && (
        <View style={styles.noteComposer}>
          {/* Commenter badge */}
          <View style={styles.commenterRow}>
            <View style={[styles.commenterBadge, { backgroundColor: currentUserAvatarColor }]}>
              <Text style={styles.commenterInitials}>{currentUserInitials}</Text>
            </View>
            <Text style={styles.commenterName}>{currentUserName}</Text>
          </View>

          <TextInput
            style={styles.noteInput}
            value={text}
            onChangeText={(v) => { if (v.length <= MAX_COMMENT_CHARS) setText(v); setSubmitError(""); }}
            placeholder="Add a note..."
            placeholderTextColor="#9099aa"
            multiline
            numberOfLines={2}
            textAlignVertical="top"
            maxLength={MAX_COMMENT_CHARS}
          />
          <Text style={[styles.charCounter, text.length >= MAX_COMMENT_CHARS * 0.9 && styles.charCounterWarn]}>
            {text.length}/{MAX_COMMENT_CHARS}
          </Text>

          {/* File attachment previews */}
          {attachedFiles.length > 0 && (
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 8, marginBottom: 4 }}>
              {attachedFiles.map((f) => (
                <FileChip key={f.id} file={f} onRemove={removeFile} onImagePress={setLightboxUri} />
              ))}
            </View>
          )}

          {/* Inline errors */}
          {fileError ? <Text style={styles.composerError}>{fileError}</Text> : null}
          {submitError ? <Text style={styles.composerError}>{submitError}</Text> : null}

          {/* Footer */}
          <View style={styles.composerFooter}>
            <Pressable
              style={[styles.attachBtn, attachedFiles.length >= MAX_ATTACH_FILES && styles.btnDisabled]}
              onPress={pickFile}
              disabled={attachedFiles.length >= MAX_ATTACH_FILES}
            >
              <MaterialIcons name="attach-file" size={18} color={attachedFiles.length >= MAX_ATTACH_FILES ? "#9ca3af" : "#374151"} />
              <Text style={[styles.attachBtnText, attachedFiles.length >= MAX_ATTACH_FILES && { color: "#9ca3af" }]}>
                Add file(s)
              </Text>
            </Pressable>
            <Pressable
              style={[styles.submitBtn, (!canSubmit || isSubmitting) && styles.btnDisabled, (!canSubmit || isSubmitting) && isDark && DARK_DISABLED_SUBMIT_STYLE]}
              onPress={handleSubmit}
              disabled={!canSubmit || isSubmitting}
            >
              <Text style={styles.submitBtnText}>{isSubmitting ? "Saving..." : "Submit Note"}</Text>
            </Pressable>
          </View>
        </View>
        )}

        {/* Note list */}
        <ScrollView
          style={styles.notesListScroll}
          contentContainerStyle={styles.notesListContent}
          showsVerticalScrollIndicator
          nestedScrollEnabled
        >
          {notes.map((note) => (
            <View key={note.id} style={styles.noteItem}>
              <View style={styles.noteHeader}>
                <View style={styles.noteAuthorRow}>
                  <View style={[styles.avatar, { backgroundColor: note.avatarColor }]}>
                    <Text style={styles.avatarText}>{note.authorInitials}</Text>
                  </View>
                  <Text style={styles.noteAuthorName}>{note.authorName}</Text>
                  <Text style={styles.noteAuthorRole}>{note.role}</Text>
                </View>
                <Text style={styles.noteTime}>{formatDateWithTimezoneNoYear(note.time)}</Text>
              </View>
              {/* HyperlinkText auto-detects URLs in saved note body � always active, never gated by ticket status */}
              {note.body ? (
                <HyperlinkText
                  text={note.body}
                  style={styles.noteBody}
                  linkStyle={{ color: "#1a56db", textDecorationLine: "underline", fontWeight: "600" }}
                />
              ) : null}
              {note.images && note.images.length > 0 && (
                <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 8 }}>
                  {note.images.map((url, idx) => (
                    <AttachmentThumbnail key={idx} url={url} onImagePress={setLightboxUri} />
                  ))}
                </View>
              )}
            </View>
          ))}
        </ScrollView>
      </View>

      {/* Lightbox */}
      {lightboxUri !== null && (
        <Modal transparent animationType="fade" onRequestClose={() => setLightboxUri(null)}>
          <Pressable style={styles.lightboxOverlay} onPress={() => setLightboxUri(null)}>
            <Image source={{ uri: lightboxUri }} style={styles.lightboxImg} resizeMode="contain" />
          </Pressable>
          <Pressable style={styles.lightboxClose} onPress={() => setLightboxUri(null)} hitSlop={12}>
            <MaterialIcons name="close" size={24} color="#ffffff" />
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

// ?? Activity Stream (sidebar) ?????????????????????????????????????????????????

type ActivityStreamProps = {
  activity: ActivityItem[];
  currentUserName: string;
  currentUserInitials: string;
  onSubmit: (text: string, images: string[]) => Promise<boolean>;
  /** When true, hides the comment input form */
  isReadOnly?: boolean;
  /** When true, hides the ServiceNow logs section entirely (e.g. for Access Request tickets) */
  hideServiceNowSection?: boolean;
  showServiceNowLogs?: boolean;
  serviceNowLogs?: Array<{
    id: number;
    operation: string;
    status: string;
    retryCount: number;
    errorCode?: string | null;
    errorMessage?: string | null;
  }>;
  serviceNowLogsLoading?: boolean;
  serviceNowLogsError?: string | null;
};

const ACTIVITY_AVATAR_COLORS: Record<ActivityItem["type"], string> = {
  user: "#005CBC",
  system: "#656565",
  sustain: "#7871E2",
  servicenow: "#0078A7",
};

const SYSTEM_ICON: Record<ActivityItem["type"], string> = {
  system: "settings",
  servicenow: "sync",
  sustain: "support-agent",
  user: "person",
};

const getBodyStyle = (type: ActivityItem["type"]) => {
  switch (type) {
    case "system":     return [styles.aiBody, styles.aiBodySystem];
    case "servicenow": return [styles.aiBody, styles.aiBodyServiceNow];
    case "sustain":    return [styles.aiBody, styles.aiBodySustain];
    case "user":
    default:           return [styles.aiBody, styles.aiBodyDefault];
  }
};

export const ActivityStream: React.FC<ActivityStreamProps> = ({
  activity,
  currentUserName,
  currentUserInitials,
  onSubmit,
  isReadOnly,
  hideServiceNowSection,
  showServiceNowLogs,
  serviceNowLogs = [],
  serviceNowLogsLoading,
  serviceNowLogsError,
}) => {
  const scrollRef = useRef<ScrollView>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const isUserScrollingRef = useRef(false);
  const hasMounted = useRef(false);
  // Tracks whether the bottom-anchored scroll has run against real (non-zero) dimensions
  const initialScrollDoneRef = useRef(false);

  const [text, setText] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [fileError, setFileError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lightboxUri, setLightboxUri] = useState<string | null>(null);

  const isDark = useTheme().resolvedTheme === "dark";

  const sorted = [...activity].reverse();

  // Show banner when user has scrolled away from the top (to view older activities)
  // Hide banner when user is at the absolute top (oldest activities visible)
  const shouldShowBanner = scrollPosition > 50;

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      isUserScrollingRef.current = false;
    }

    // Keep re-scrolling to the bottom on every layout/content-size update until we
    // have real (non-zero) measurements — the very first run fires before the
    // ScrollView has measured, so a single scroll attempt lands at position 0.
    if (!initialScrollDoneRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: false });
      }, 50);
      if (contentHeight > 0 && scrollViewHeight > 0) {
        initialScrollDoneRef.current = true;
      }
    }
  }, [contentHeight, scrollViewHeight]);

  // Scoped separately to only the `activity` array — so unrelated re-layouts
  // (banner toggling, image loads changing contentHeight/scrollViewHeight)
  // never trigger an unwanted scroll, but any real data change always does.
  useEffect(() => {
    if (!initialScrollDoneRef.current) return; // handled by the mount effect above
    setTimeout(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    }, 50);
  }, [activity]);

  const scrollToLatest = useCallback(() => {
    // Scroll to the BOTTOM of the activity list (where latest comments are)
    isUserScrollingRef.current = true;
    scrollRef.current?.scrollToEnd({ animated: true });
    // Reset flag after animation completes
    setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 600);
  }, []);

  const scrollToOlder = useCallback(() => {
    // Scroll to the TOP of the activity list (where older comments are)
    isUserScrollingRef.current = true;
    scrollRef.current?.scrollTo({ y: 0, animated: true });
    // Reset flag after animation completes
    setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 600);
  }, []);

  /** Process picked files for ActivityStream */
  const processPickedFilesActivity = useCallback(async (pickedFiles: Array<{
    name: string; uri: string; mimeType: string; size: number;
  }>) => {
    const newFiles: AttachedFile[] = [];
    let err = "";
    for (const picked of pickedFiles) {
      const ext = getFileExtension(picked.name);
      if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
        err = "File type not supported. Please upload .jpg, .jpeg, .heic, .png, .pdf, .xlsx, or .docx. files only.";
        continue;
      }
      if (picked.size > MAX_FILE_BYTES) {
        err = "File exceeds 10MB limit. Please select a smaller file.";
        continue;
      }
      if (attachedFiles.length + newFiles.length >= MAX_ATTACH_FILES) {
        err = `You can attach up to ${MAX_ATTACH_FILES} files per comment.`;
        break;
      }
      const isImg = IMAGE_MIME_TYPES.includes(picked.mimeType) || [".jpg", ".jpeg", ".heic", ".png"].includes(ext);
      newFiles.push({
        id: `af-${Date.now()}-${newFiles.length}`,
        uri: picked.uri,
        originalName: picked.name,
        extension: ext,
        isUploading: true,
        isImage: isImg,
      });
    }
    if (err) setFileError(err);
    if (newFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newFiles]);
      const api = createApiClient();
      for (const f of newFiles) {
        try {
          const result = await uploadTicketFile(api, f.uri, f.originalName, "ticket-comment");
          if (result) {
            setAttachedFiles((prev) =>
              prev.map((p) =>
                p.id === f.id ? { ...p, uploadedFileName: result.fileName, fileUrl: result.fileUrl, isUploading: false } : p
              )
            );
          } else {
            setAttachedFiles((prev) => prev.filter((p) => p.id !== f.id));
            setFileError("File upload failed. Please try again.");
          }
        } catch (uploadErr) {
          setAttachedFiles((prev) => prev.filter((p) => p.id !== f.id));
          setFileError(isFileTooLargeError(uploadErr) ? "File exceeds 10MB limit. Please select a smaller file." : "File upload failed. Please try again.");
        }
      }
    }
  }, [attachedFiles.length]);

  const pickFile = useCallback(() => {
    setFileError("");
    if (attachedFiles.length >= MAX_ATTACH_FILES) {
      setFileError(`You can attach up to ${MAX_ATTACH_FILES} files per comment.`);
      return;
    }
    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".jpg,.jpeg,.heic,.png,.pdf,.xlsx,.docx";
      input.multiple = true;
      input.onchange = async (e: any) => {
        const files: FileList = e.target.files;
        if (!files || files.length === 0) return;
        const picked: Array<{ name: string; uri: string; mimeType: string; size: number }> = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const uri = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          picked.push({ name: file.name, uri, mimeType: file.type, size: file.size });
        }
        processPickedFilesActivity(picked);
      };
      input.click();
    } else {
      launchImageLibrary(
        { mediaType: "mixed", quality: 1, selectionLimit: MAX_ATTACH_FILES - attachedFiles.length, includeBase64: true },
        async (response) => {
          if (response.didCancel || !response.assets) return;
          const picked = response.assets.map((asset) => {
            const rawFileName = String(asset.fileName || asset.uri?.split("/").pop() || "file");
            const mimeType = (asset.type || "").toLowerCase();
            const resolvedMime = mimeType || "application/octet-stream";
            const uri = asset.base64 ? `data:${resolvedMime};base64,${asset.base64}` : String(asset.uri || "");
            const size = asset.base64 ? Math.ceil(asset.base64.length * 0.75) : (asset.fileSize ?? 0);
            return { name: rawFileName, uri, mimeType: resolvedMime, size };
          });
          processPickedFilesActivity(picked);
        }
      );
    }
  }, [attachedFiles.length, processPickedFilesActivity]);

  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
    setFileError("");
  }, []);

  const canSubmit = text.trim().length > 0 || attachedFiles.length > 0;

  const handleSubmit = useCallback(async () => {
    setSubmitError("");
    if (!canSubmit) {
      setSubmitError("Comment cannot be empty.");
      return;
    }
    // ?? URL validation (Requirement 3): check before API call ?????????????
    // Only validates if the text contains an http/https attempt.
    if (text.trim()) {
      const urlError = validateTextUrls(text);
      if (urlError) {
        setSubmitError(urlError);
        return;
      }
    }
    const stillUploading = attachedFiles.some((f) => f.isUploading);
    if (stillUploading) {
      setSubmitError("Please wait for files to finish uploading.");
      return;
    }
    setIsSubmitting(true);
    try {
      const fileNames = attachedFiles
        .map((f) => f.uploadedFileName)
        .filter((n): n is string => typeof n === "string" && n.length > 0);
      const ok = await onSubmit(text, fileNames);
      if (ok) {
        setText("");
        setAttachedFiles([]);
        setFileError("");
        // Auto-scroll to latest activity after successful comment submission
        isUserScrollingRef.current = true;
        setTimeout(() => {
          scrollRef.current?.scrollToEnd({ animated: true });
          // Reset flag after animation completes
          setTimeout(() => {
            isUserScrollingRef.current = false;
          }, 600);
        }, 100);
      } else {
        setSubmitError("Unable to submit your comment. Please try again.");
      }
    } catch {
      setSubmitError("Unable to submit your comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, text, attachedFiles, onSubmit]);

  return (
    <View style={styles.activityWrap}>
      {/* Header */}
      <View style={styles.activityHeader}>
        <View style={styles.sectionTitleRow}>
          <MaterialIcons name="schedule" size={16} color="#374151" />
          <Text style={styles.activityTitle}>Activity</Text>
        </View>
        <Text style={styles.activityCount}>All - {activity.length}</Text>
      </View>

      {showServiceNowLogs && !hideServiceNowSection ? (
        <View style={{ paddingHorizontal: 16, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
          <Text style={{ fontSize: 13, fontWeight: "700", color: "#1f2937", marginBottom: 8 }}>ServiceNow Logs</Text>
          {serviceNowLogsLoading ? (
            <Text style={{ fontSize: 12, color: "#6b7280" }}>Loading logs...</Text>
          ) : serviceNowLogsError ? (
            <Text style={{ fontSize: 12, color: "#b91c1c" }}>{serviceNowLogsError}</Text>
          ) : serviceNowLogs.length === 0 ? (
            <Text style={{ fontSize: 12, color: "#6b7280" }}>No ServiceNow log entries for this ticket yet.</Text>
          ) : (
            serviceNowLogs.slice(0, 5).map((log) => (
              <View key={log.id} style={{ flexDirection: "row", justifyContent: "space-between", gap: 8, backgroundColor: "#f8fafc", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 8, marginBottom: 6 }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 12, fontWeight: "700", color: "#1f2937" }}>{log.operation}</Text>
                  <Text style={{ fontSize: 11, color: "#4b5563" }} numberOfLines={2}>{log.errorMessage || log.errorCode || "No error message"}</Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={{ fontSize: 11, fontWeight: "700", color: "#1d4ed8" }}>{log.status}</Text>
                  <Text style={{ fontSize: 10, color: "#6b7280" }}>Retry {log.retryCount}</Text>
                </View>
              </View>
            ))
          )}
        </View>
      ) : null}

      {/* Activity list � newest first */}
      {/* "Click Here to View Older Information" banner – shown when user scrolls up */}
      {shouldShowBanner && activity.length > 0 && (
        <Pressable 
          style={styles.newActivityBanner}
          onPress={scrollToOlder}
        >
          <MaterialIcons name="arrow-upward" size={16} color="#1d4ed8" />
          <Text style={styles.newActivityBannerText}>Click Here to View Older Information</Text>
        </Pressable>
      )}


      <ScrollView
        ref={scrollRef}
        style={styles.activityBody}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={(width, height) => setContentHeight(height)}
        onLayout={(e) => setScrollViewHeight(e.nativeEvent.layout.height)}
        onScroll={(e) => setScrollPosition(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
      >
        {sorted.map((item) => {
          const isSystem = item.type === "system" || item.type === "servicenow";
          return (
            <View key={item.id} style={styles.activityItem}>
              <View style={[styles.aiIcon, { backgroundColor: ACTIVITY_AVATAR_COLORS[item.type] }]}>
                {isSystem ? (
                  <MaterialIcons name={SYSTEM_ICON[item.type] as any} size={14} color="#ffffff" />
                ) : (
                  <Text style={styles.aiIconText}>{item.authorInitials}</Text>
                )}
              </View>
              <View style={styles.aiContent}>
                <View style={styles.aiTop}>
                  <View style={styles.aiAuthorRow}>
                    {!isSystem && <Text style={styles.aiAuthor}>{item.authorName}</Text>}
                    {item.role ? <Text style={styles.aiRole}>{item.role}</Text> : null}
                  </View>
                  <Text style={styles.aiTime}>{formatDateWithTimezoneNoYear(item.time)}</Text>
                </View>
                {/* HyperlinkText auto-detects URLs in activity content and makes them tappable.
                    Links are ALWAYS active � no isReadOnly gate � per Requirement 5. */}
                {item.content ? (
                  <HyperlinkText
                    text={item.content}
                    style={getBodyStyle(item.type) as any}
                    linkStyle={{ color: "#1a56db", textDecorationLine: "underline", fontWeight: "600" }}
                  />
                ) : null}
                {item.images && item.images.length > 0 && (
                  <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 8 }}>
                    {item.images.map((url, idx) => (
                      <AttachmentThumbnail key={idx} url={url} onImagePress={setLightboxUri} />
                    ))}
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </ScrollView>
      {!isReadOnly && (
      <View style={styles.commentComposer}>
        {/* Commenter badge */}
        <View style={styles.commenterRow}>
          <View style={[styles.commenterBadge, { backgroundColor: ACTIVITY_AVATAR_COLORS.sustain }]}>
            <Text style={styles.commenterInitials}>{currentUserInitials}</Text>
          </View>
          <Text style={styles.commenterName}>{currentUserName}</Text>
        </View>

        {/* Text area */}
        <TextInput
          style={styles.commentInput}
          value={text}
          onChangeText={(v) => { if (v.length <= MAX_COMMENT_CHARS) setText(v); setSubmitError(""); }}
          placeholder="Add a comment. Sustain Team comments will sync to ServiceNow within 5 minutes."
          placeholderTextColor="#9099aa"
          multiline
          numberOfLines={3}
          textAlignVertical="top"
          maxLength={MAX_COMMENT_CHARS}
        />
        <Text style={[styles.charCounter, text.length >= MAX_COMMENT_CHARS * 0.9 && styles.charCounterWarn]}>
          {text.length}/{MAX_COMMENT_CHARS}
        </Text>

        {/* Attached file previews */}
        {attachedFiles.length > 0 && (
          <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 8, marginBottom: 4 }}>
            {attachedFiles.map((f) => (
              <FileChip key={f.id} file={f} onRemove={removeFile} onImagePress={setLightboxUri} />
            ))}
          </View>
        )}

        {/* Inline error messages */}
        {fileError ? <Text style={styles.composerError}>{fileError}</Text> : null}
        {submitError ? <Text style={styles.composerError}>{submitError}</Text> : null}

        {/* Footer: attach + submit */}
        <View style={styles.composerFooter}>
          <Pressable
            style={[styles.attachBtn, attachedFiles.length >= MAX_ATTACH_FILES && styles.btnDisabled]}
            onPress={pickFile}
            disabled={attachedFiles.length >= MAX_ATTACH_FILES}
          >
            <MaterialIcons
              name="attach-file"
              size={18}
              color={attachedFiles.length >= MAX_ATTACH_FILES ? "#9ca3af" : "#374151"}
            />
            <Text style={[styles.attachBtnText, attachedFiles.length >= MAX_ATTACH_FILES && { color: "#9ca3af" }]}>
              Add file(s)
            </Text>
          </Pressable>
          <Pressable
            style={[styles.submitBtn, (!canSubmit || isSubmitting) && styles.btnDisabled, (!canSubmit || isSubmitting) && isDark && DARK_DISABLED_SUBMIT_STYLE]}
            onPress={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            <Text style={styles.submitBtnText}>{isSubmitting ? "Posting..." : "Submit Comment"}</Text>
          </Pressable>
        </View>
      </View>
      )}

      {/* Lightbox */}
      {lightboxUri !== null && (
        <Modal transparent animationType="fade" onRequestClose={() => setLightboxUri(null)}>
          <Pressable style={styles.lightboxOverlay} onPress={() => setLightboxUri(null)}>
            <Image source={{ uri: lightboxUri }} style={styles.lightboxImg} resizeMode="contain" />
          </Pressable>
          <Pressable style={styles.lightboxClose} onPress={() => setLightboxUri(null)} hitSlop={12}>
            <MaterialIcons name="close" size={24} color="#ffffff" />
          </Pressable>
        </Modal>
      )}
    </View>
  );
};
